/**
 * Tier-1 — the platform admin's CATALOG screen (`platformRole === 'admin'`).
 *
 * The UI half of the whitelabel change. Plans, credit packs, the charged
 * currency and the credit markup used to be frozen into migrations and a
 * constant in `server/credits.ts`; they are now rows, edited here. Without this
 * screen the supported path to set a price was curl, and the unsupported one
 * was hand-run SQL against production — which CLAUDE.md forbids, and which is
 * exactly what `/api/admin/billing/catalog` exists to replace.
 *
 * Four things this file exists to keep straight:
 *
 *  1. **Editing a row is not publishing it.** A plan or pack with no Stripe
 *     Price cannot be bought — `POST /credits/checkout` 409s — so a saved row
 *     is only half the job. Publish is a separate, explicit button, because a
 *     re-mint is a provider write with a real consequence: Stripe Prices are
 *     immutable, so changing a price ORPHANS the old one for existing
 *     subscribers. Unpublished rows carry a badge rather than a silent 409
 *     discovered at a customer's checkout.
 *  2. **The credit rate is the margin, not a conversion.**
 *     `creditRateMinorPerUsd` is what the customer pays per US$1.00 of LLM
 *     credit. It is stated in those words next to the field, with the implied
 *     price rendered, because "550" reads like an exchange rate and is not one.
 *  3. **Minor units everywhere, never `/100`.** Every price on the wire is
 *     minor units of that row's own currency. Admins type major units; the
 *     conversion goes through `parseMajorToMinor` / `formatCurrencyMinor`,
 *     which follow the currency's exponent. A literal `/100` here is a 100×
 *     error on JPY and a 10× one on BHD.
 *  4. **A grant-affecting edit does not reach existing subscribers.**
 *     Entitlements are materialized at subscription events, so changing an
 *     included budget or a quota changes what NEW subscribers get and leaves
 *     current ones where they were until something re-applies. Stated in the
 *     section rather than left to be discovered.
 *
 * `free` is editable but cannot be deactivated: it is the implicit fallback for
 * every tenant without a subscription and `getFree()` throws when it is
 * missing. The server refuses it too — this only avoids offering the click.
 *
 * Copy lives in the `billing` i18n namespace (`catalog.*`, all four locales),
 * matching PromotionsPanel — this renders in the same fully-localized shell.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { pinnedBatch, inBatch, type PinnedBatch } from '@papan/api-client'
import type { BudgetCap } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import {
  useConfirm, useToast, useTheme, getThemeClass, DirtyBar, Tooltip, Modal, ToggleSwitch,
  usePlanEditBar, useRegisterSection, useDraftState, useDraftInFlight, type PlanEditBar,
} from '@papan/core-ui/ui'
import {
  formatCurrencyMinor, planBlocksCreate, PLATFORM_DATE_LOCALE,
  resolvePlanRateLimit, normalisePlanRateLimits, PLAN_RATE_LIMIT_TIERS, MAX_PLAN_RATE_LIMIT,
  type PlanRateLimitTier,
  resolvePlanFieldDefaults, inheritedFieldState,
  // Static catalogs the plan-CREATE draft mode seeds its blank grids from
  // (there is no persisted plan to fetch by id yet — see the `draftValue`
  // contracts on `FeaturePricingEditor` / `LlmPricing`).
  PER_TENANT_PRICEABLE, MODEL_CATALOG,
} from '@papan/types'
import {
  catalogAdminApi,
  type AdminPlanView,
  type AdminPlanPriceView,
  type AdminPackView,
  type BillingSettingsView,
  type RateEpochView,
  type PlanFeaturePricingRow,
} from '../client/catalog-admin.js'
import {
  parsePrice,
  priceToInput,
  parseOptionalInt,
  previewCreditBillingMicro,
  fmtBillingMicro,
  isNeverSold,
  isValidRateField,
  modelPriceFieldStatus,
} from './catalog-units.js'
import { formatBillingMicro, formatStorage } from './money.js'
import { PlanEditor, clearPlanCreateDrafts } from './PlanEditor.js'
import type { PlanSavers, PlanScalarSaveResult } from './plan-editor-scope.js'

/** The translate function shape the helpers below need.
 *  Exported so `PlanEditor.tsx` (the extracted plan Edit-modal body) can type
 *  its own `t` prop against the same shape rather than a second declaration. */
export type T = (key: string, opts?: Record<string, unknown>) => string

// `usePlanEditBar` / `useRegisterSection` / `PlanEditBar` moved to
// `@papan/core-ui` so the per-tenant Plan tab (in `@papan/orgs`) can share the
// one-Save-bar controller. Imported above.

/**
 * Thrown by a bar-registered section to HALT the aggregate save without it being
 * an error — the operator cancelled a mid-batch confirm (the billing-rhythm
 * reprice is the one that asks). The host's `runBarSave` catches it, skips the
 * green "saved" toast (the throw already did) AND the red error toast. A real
 * failure throws an ordinary Error and still surfaces.
 */
class BarAbort extends Error {
  constructor() {
    super('bar-abort')
    this.name = 'BarAbort'
  }
}

/**
 * A hoverable "ⓘ" that reveals one setting's explanation on demand, so the
 * dense plan editor carries its guidance without a paragraph under every field.
 * `always` is required — the Tooltip otherwise fires only when its trigger text
 * overflows, and a fixed-size icon never does.
 */
export function InfoHint({ text }: { text: string }) {
  return (
    <Tooltip content={text} always placement="top" className="inline-flex items-center align-middle">
      <span
        aria-label={text}
        tabIndex={0}
        className="inline-flex items-center justify-center h-3.5 w-3.5 rounded-full border themed-border themed-text-muted text-[9px] font-semibold leading-none cursor-help select-none"
      >
        i
      </span>
    </Tooltip>
  )
}

// ─── Drafts ───────────────────────────────────────────────────────────

/** Exported for `PlanEditor.tsx` — the scalar-fields draft it renders is owned
 *  and seeded by this file (`planDraft` below), not duplicated there. */
export interface PlanDraft {
  name: string
  audience: string
  // Price/free-credits/extra-allowance are NO LONGER here. A plan is priced PER
  // CURRENCY (`billing_plan_prices`), and all of that is edited in the dialog's
  // `PlanPrices` table — one row per currency, each with its own amounts — so it
  // has its own draft state and Save, like the other sub-editors.
  /** Bytes. The column is NOT NULL on both `billing_plans` and `tenants`, so
   *  unlike a cap this has no null state — hence `GbInput`, not `CapInput`. */
  storageBytes: number
  /** `null` = fall back to the platform default, NOT unlimited. */
  concurrency: BudgetCap
  /**
   * O7 relative compute share (#539). NOT NULL with a default of 1, so — like
   * `includedSeatsText` and unlike `concurrency` — there is no null state and
   * no "unset" to express. Only the RATIO between plans means anything; every
   * plan at the same number is plain round-robin.
   *
   * Held as TEXT for the same reason `includedSeatsText` is: the box must
   * tolerate being empty mid-typing, which a `number` draft cannot express.
   * Parsed and range-checked at save.
   */
  fairnessWeightText: string
  includedSeatsText: string
  maxSeatsText: string
  sortText: string
  isActive: boolean
}

interface PackDraft {
  name: string
  currency: string
  priceText: string
  sortText: string
  isActive: boolean
}

interface SettingsDraft {
  currency: string
  rateText: string
  floorText: string
  ceilingText: string
}

/**
 * The plan's price row in ONE currency — the deployment's own.
 *
 * A plan may be priced in several (`billing_plan_prices` is keyed on
 * `(plan_id, currency)`), but exactly one of them is load-bearing here: the one
 * the deployment CHARGES in, which is what `applyEntitlements` requires and
 * what every screen quotes. The others are shown as chips and can be removed;
 * adding a market is a price-book API call, deliberately not a control on the
 * row an operator uses every day.
 *
 * `null` = the plan is UNPRICED in that currency, which is not zero: it refuses
 * rather than granting nothing. The editor says so in words.
 */
export function priceFor(p: AdminPlanView, currency: string): AdminPlanPriceView | null {
  return p.prices.find((x) => x.currency === currency.toLowerCase()) ?? null
}

/** Exported so `PlanEditor.tsx` can re-derive "the plan's own draft" for its
 *  scalar-fields dirty check — the equivalent of `pBase[id]` without needing
 *  `PlansSection`'s snapshot state. See the scalar section in that file. */
export const planDraft = (p: AdminPlanView): PlanDraft => ({
  name: p.name,
  audience: p.audience,
  storageBytes: p.storageQuotaBytes,
  concurrency: p.agentConcurrencyCap,
  fairnessWeightText: String(p.fairnessWeight),
  includedSeatsText: String(p.includedSeats),
  // Blank = unlimited (null), NOT 0. `0` is a real "nobody" ceiling, so it must
  // be typed deliberately, never be the empty-box fallback.
  maxSeatsText: p.maxSeats == null ? '' : String(p.maxSeats),
  sortText: String(p.sortOrder),
  isActive: p.isActive,
})

/**
 * `<PlanEditor>`'s save operations for `scope.kind === 'plan'` — see
 * `plan-editor-scope.ts`'s docblock for why these are injected rather than
 * resolved from `scope.kind` inside the editor.
 *
 * P1 Task 3: called by `PlanUploads` / `PlanCycle` / `PlanRateLimitsEditor`
 * and by `PlanEditor.tsx`'s own scalar-fields section (moved there from this
 * file's `PlansSection`, which used to own that registration via `savePlans`
 * — see `PlanEditor.tsx`'s "scalar section" comment for why the move was
 * necessary rather than cosmetic).
 */
function planSavers(p: AdminPlanView): PlanSavers {
  return {
    async saveScalars(patch, batch) {
      if (patch.includedSeats === null) {
        // `PlanPatch.includedSeats` is NOT NULL server-side (a preset's own
        // seat count has no "inherit" state, unlike a tenant's per-tenant
        // override) — `null` only ever comes from `tenant` scope's
        // `InheritedCapField`, which this saver never receives from.
        throw new Error('saveScalars: a plan\'s own included seats cannot be "inherit"')
      }
      const includedSeats = patch.includedSeats   // narrowed above (never null here)
      const res = await inBatch(batch, () => catalogAdminApi.updatePlan(p.id, {
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(includedSeats !== undefined ? { includedSeats } : {}),
        ...(patch.storageBytes !== undefined ? { storageQuotaBytes: patch.storageBytes } : {}),
        ...(patch.concurrency !== undefined ? { agentConcurrencyCap: patch.concurrency } : {}),
        ...(patch.maxSeats !== undefined ? { maxSeats: patch.maxSeats } : {}),
      }))
      return { entitlementsStale: res.entitlementsStale }
    },
    async saveUploads(canCreate, batch) {
      // MERGE patch — see `PlanUploads`' own save for why `null` (not `false`)
      // is what UN-pauses: absence is the unrestricted state.
      await inBatch(batch, () => catalogAdminApi.updatePlan(p.id, {
        featuresPatch: { canCreateContent: canCreate === false ? false : null },
      }))
    },
    async saveCycle(count, unit, batch) {
      await inBatch(batch, () => catalogAdminApi.updatePlan(p.id, { cycleCount: count, cycleUnit: unit }))
    },
    async saveRateLimits(limits, batch) {
      await inBatch(batch, () => catalogAdminApi.updatePlan(p.id, { featuresPatch: { rateLimits: limits } }))
    },
    // Free/extra/grace caps and enforcement mode are TENANT-only axes (Task 6,
    // P4) — a catalog plan has no override record and no enforcement mode of
    // its own to write. Neither `tenantCaps` nor `tenantEnforcement` render in
    // `plan` scope, so these should never be called; throw loudly rather than
    // silently no-op if that ever changes.
    async saveCaps() {
      throw new Error('saveCaps: not supported in plan scope — free/extra/grace are tenant-only axes')
    },
    async saveEnforcement() {
      throw new Error('saveEnforcement: not supported in plan scope — enforcement mode is a tenant-only axis')
    },
  }
}

/**
 * `<PlanEditor scope={{kind:'plan-create'}}>` never calls the injected
 * `savers` prop at all — it builds its own draft-mutating `PlanSavers`
 * internally (`useDraftSavers`, see that component's module docblock) and
 * persists everything through the single Confirm button instead. The prop is
 * still required by `PlanEditorProps` (the same shape `scope: 'plan'` uses),
 * so the create-modal call site below needs SOME `PlanSavers` to satisfy
 * TypeScript; this one throws if it is ever reached, so a future change that
 * makes `plan-create` start reading it fails loudly instead of silently
 * writing nothing.
 */
const noopPlanSavers: PlanSavers = {
  saveScalars: async () => { throw new Error('plan-create scope must not call the injected savers') },
  saveUploads: async () => { throw new Error('plan-create scope must not call the injected savers') },
  saveCycle: async () => { throw new Error('plan-create scope must not call the injected savers') },
  saveRateLimits: async () => { throw new Error('plan-create scope must not call the injected savers') },
  saveCaps: async () => { throw new Error('plan-create scope must not call the injected savers') },
  saveEnforcement: async () => { throw new Error('plan-create scope must not call the injected savers') },
}


const packDraft = (k: AdminPackView): PackDraft => ({
  name: k.name,
  currency: k.currency,
  priceText: priceToInput(k.priceMinor, k.currency),
  sortText: String(k.sortOrder),
  isActive: k.isActive,
})

/**
 * `rateText` is MAJOR units, like every other money field on this screen.
 *
 * It used to be the raw minor-unit integer — an operator typed `550` into a
 * card whose two neighbours took `10.00` and `10000.00`, on the one number that
 * sets the deployment's gross margin. Mixed units inside a single card is how a
 * 100× error gets typed and saved without anything looking wrong. The overage
 * rate — the same shape of number, minor units of the plan currency per US$1.00
 * — was already major-unit on this very screen, so this field was the odd one
 * out even locally.
 *
 * `parsePrice`/`priceToInput` are the pair everything else here uses, and they
 * go through `parseMajorToMinor`/`formatCurrencyMinor`, so the exponent comes
 * from the currency rather than a hardcoded 100. The wire value is unchanged.
 */
// Codex F16 (round 20): `s.currency` was `undefined` after the 0177
// column rename (`billing_settings.currency` → `billing_currency`) —
// the server's repo returns `billingCurrency`, the client DTO now
// matches. The local `SettingsDraft` keeps its own `currency` field
// name for wire-body back-compat with `updateSettings`, which still
// accepts `{currency: ...}` on the write side.
const settingsDraft = (s: BillingSettingsView): SettingsDraft => ({
  currency: s.billingCurrency,
  rateText: priceToInput(s.creditRateMinorPerUsd, s.billingCurrency),
  floorText: priceToInput(s.purchaseFloorMinor, s.billingCurrency),
  ceilingText: priceToInput(s.purchaseCeilingMinor, s.billingCurrency),
})

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** No staged edits — one stable, never-mutated value for the drafts' fallback (#897). */
const NO_EDITS = Object.freeze({}) as Record<string, never>

/** `base` with each id's staged fields laid over it — the per-row drafts a
 *  screen renders, built from a DELTA so a reload shows through every field
 *  nobody touched (#897). */
// #897 — a field edited BACK to its loaded value is no edit. The touched maps
// below drop it: kept, it pinned the old value, so a later reload of that field
// never showed (the deck stays mounted) and Save wrote it over someone else's
// change.
const sameJson = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
/** The fields of `patch` that differ from `base` (all of them with no base). */
function changedFields<D extends object>(patch: Partial<D>, base: D | null | undefined): Partial<D> {
  if (!base) return patch
  const out: Partial<D> = {}
  for (const [k, v] of Object.entries(patch) as [keyof D, D[keyof D]][]) {
    if (!sameJson(v, base[k])) out[k] = v
  }
  return out
}
/** `touched` with `id` set to `value`, or without `id` when `value` is
 *  `undefined` or an empty patch (nothing left to change). */
function withEdit<V>(touched: Record<string, V>, id: string, value: V | undefined): Record<string, V> {
  const empty = value === undefined || (typeof value === 'object' && value !== null && Object.keys(value).length === 0)
  if (!empty) return { ...touched, [id]: value as V }
  if (!(id in touched)) return touched
  const { [id]: _dropped, ...rest } = touched
  return rest
}

function overlay<D>(base: Record<string, D>, touched: Record<string, Partial<D>>): Record<string, D> {
  const out: Record<string, D> = {}
  for (const [id, b] of Object.entries(base)) out[id] = touched[id] ? { ...b, ...touched[id] } : b
  return out
}

// ─── Screen ───────────────────────────────────────────────────────────

export function CatalogAdmin({
  isPlatformAdmin,
  canEditPricing = true,
  header,
  defaultPresetId,
  onSetDefaultPreset,
  onPropagate,
  onGrantsChanged,
  renderPlanTenants,
  openPlanId,
  onOpenPlan,
  onClosePlan,
}: {
  isPlatformAdmin: boolean
  /**
   * Which plan's editor is open, FROM THE URL (`…/pricebook/<planId>`).
   *
   * Was `useState` here, which put the editor below the whole table and made
   * it unlinkable. Same reasoning as the customer detail page: a plan's editor
   * is four sub-editors and a screen's worth of one subject, and "look at this
   * plan" is a thing an operator pastes into a ticket.
   */
  openPlanId?: string
  onOpenPlan?: (planId: string) => void
  onClosePlan?: () => void
  /** Phase 0 (D2): `pricing:write` — plans, credit packs, publish-to-Stripe. */
  canEditPricing?: boolean
  /**
   * The merged price book's own heading, replacing this component's.
   *
   * Phase 1 unified the DESTINATION and stacked the two old screens; phase 4
   * merges their CONTENT. Two `<h2>`s one above the other is what the stack
   * looked like, and ScrollSpy builds its rail from heading text — so the
   * duplicate was visible in the navigation too, not only on the page.
   */
  header?: ReactNode
  /**
   * Which preset new customers are seeded from — `platform_settings.default_plan_id`.
   *
   * ── Why this arrives as a prop rather than being fetched here ────────────
   *
   * The value lives on `platform_settings`, whose client is in `@papan/orgs`,
   * which depends on `@papan/billing`. Fetching it here would either close that
   * cycle or mean a SECOND client for a route another package already owns —
   * two implementations of one read, which is how two screens come to disagree
   * about one stored value. The console composes it, exactly like the metered
   * grid above.
   *
   * `undefined` (not `null`) means the host did not wire it, and the column is
   * not rendered at all: `null` is a real state — no default configured.
   */
  defaultPresetId?: string | null
  /**
   * Make a preset the deployment default.
   *
   * Gated on `settings:write`, NOT `pricing:write` — the action writes
   * `platform_settings`, and CLAUDE.md's rule is to gate by what an action DOES,
   * not where it renders. That is why it is a separate prop from
   * `canEditPricing`: the two grants are genuinely distinct for the read-only
   * tier (a `viewer`/`guest` seat holds neither), and the gate keeps them apart
   * so it stays correct if the matrix ever regains a role that holds one and not
   * the other. (Before 2026-09-03 the `member` seat was exactly such a role —
   * `pricing:write`, `settings:read` — able to re-price a preset but not point
   * provisioning at it; member is now full write.) Absent ⇒ the badge renders
   * read-only.
   */
  onSetDefaultPreset?: (planId: string) => Promise<void>
  /**
   * Open the propagation prompt for one preset (D1b).
   *
   * A callback, not a dialog, for the same reason `defaultPresetId` is a prop:
   * the prompt reads `budgetApi`, which lives in `@papan/orgs` — and orgs depends
   * on billing, so importing it here would close the cycle. Absent ⇒ no button,
   * which is also the read-only state.
   */
  onPropagate?: (plan: { id: string; name: string }) => void
  /**
   * Auto-apply (F3): fired after a plan save that changed a GRANT-AFFECTING
   * value, so the host can offer to push it to the tenants on the plan
   * (preserving overwritten axes). Same cycle reasoning as `onPropagate` — the
   * apply reads `budgetApi` in `@papan/orgs`, so the host owns it. Absent ⇒ the
   * existing stale banner is the only surface.
   */
  onGrantsChanged?: (plan: { id: string; name: string }) => void
  /**
   * The linked-tenant list + "Link tenants" control for the plan modal's
   * Tenant-specific controls section (redesign follow-up).
   *
   * A slot, not built here, for the same cycle reason as `onPropagate`: listing
   * the tenants on a plan reads `budgetApi.listTenants` in `@papan/orgs`, and orgs
   * depends on billing — so the console composes it. Absent ⇒ only the count and
   * the set-default / apply controls render (the pre-slot behaviour).
   */
  renderPlanTenants?: (plan: { id: string; name: string }) => ReactNode
}) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const confirm = useConfirm()

  const [settings, setSettings] = useState<BillingSettingsView | null>(null)
  const [currencies, setCurrencies] = useState<string[]>([])
  const [billingEnabled, setBillingEnabled] = useState(false)
  const [plans, setPlans] = useState<AdminPlanView[] | null>(null)
  const [packs, setPacks] = useState<AdminPackView[] | null>(null)
  /** Under manual billing the plan editor hides the extra-seat provider fields
   *  (price-per-seat, extra allowance) — they do nothing there. */
  const [planAuthority, setPlanAuthority] = useState<'provider' | 'manual'>('provider')

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // ONE lever for the whole page now. It used to need two, because the
  // deployment-settings block living here belonged to a different capability
  // area (`settings` vs `pricing`) and a single flag would have let a `member`
  // seat edit the credit markup through the wrong door. Phase 5 moved that
  // block to Platform settings, so everything left on this screen is a price.
  const busy = saving || !canEditPricing
  const [syncing, setSyncing] = useState(false)

  // Staged plan / pack fields, kept across a phone/desktop layout swap (#897):
  // only what was TOUCHED is stored (per id), laid over the live baseline, so
  // a `load` shows through every untouched field and never wipes an edit — the
  // new screen's first load after a swap must not drop the draft it carried.
  // A save or Discard drops them.
  const [pBase, setPBase] = useState<Record<string, PlanDraft>>({})
  const [pTouched, setPTouched, { clear: clearPTouched }] = useDraftState<Record<string, Partial<PlanDraft>>>('catalog:plans', NO_EDITS)
  const pDrafts = useMemo(() => overlay(pBase, pTouched), [pBase, pTouched])
  const [kBase, setKBase] = useState<Record<string, PackDraft>>({})
  const [kTouched, setKTouched, { clear: clearKTouched }] = useDraftState<Record<string, Partial<PackDraft>>>('catalog:packs', NO_EDITS)
  const kDrafts = useMemo(() => overlay(kBase, kTouched), [kBase, kTouched])
  /**
   * Plans whose last edit changed something the tenant carries a COPY of
   * (catalogue A4), and how many live subscribers are now on the old numbers.
   *
   * Session-scoped rather than derived from server state, deliberately: there
   * is no "stale" column to read, and there could not be a cheap one — the
   * question is "did anything change since each tenant's last entitlement
   * event", which is per tenant. What the operator needs is to be told at the
   * moment they caused it, which is exactly what this is.
   */
  const [stalePlans, setStalePlans] = useState<
    Record<string, { name: string; tenants: number; fields: string[] }>
  >({})

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // Independent reads — the settings response carries the currency list the
      // other two are edited against, but nothing here depends on ordering.
      const [s, p, k] = await Promise.all([
        catalogAdminApi.getSettings(),
        catalogAdminApi.listPlans(),
        catalogAdminApi.listPacks(),
      ])
      setSettings(s.settings)
      setCurrencies(s.supportedCurrencies)
      setBillingEnabled(s.billingEnabled)
      setPlanAuthority(s.planAuthority)
      setPlans(p.plans)
      setPacks(k.packs)

      // Scalar plan fields only — a plan's per-currency prices are edited in the
      // dialog's `PlanPrices` table, which reads `plan.prices` directly.
      const pd = Object.fromEntries(p.plans.map(x => [x.id, planDraft(x)]))
      setPBase(pd)
      const kd = Object.fromEntries(k.packs.map(x => [x.id, packDraft(x)]))
      setKBase(kd)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load catalog')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isPlatformAdmin) { setLoading(false); return }
    load()
  }, [isPlatformAdmin, load])

  const dirtyPlans = useMemo(
    () => Object.keys(pDrafts).filter(id => pBase[id] && !same(pDrafts[id], pBase[id])),
    [pDrafts, pBase],
  )
  const dirtyPacks = useMemo(
    () => Object.keys(kDrafts).filter(id => kBase[id] && !same(kDrafts[id], kBase[id])),
    [kDrafts, kBase],
  )

  // ── Saves ───────────────────────────────────────────────────────────

  /**
   * Bridges `PlanEditor.tsx`'s scalar-fields section (P1 Task 3) back to this
   * screen's stale-entitlements banner (catalogue A4) and F3 auto-apply
   * prompt. The scalar section itself now owns validation, the terminal write
   * (`planSavers(p).saveScalars`) and its own `useRegisterSection` — it used
   * to live here as `savePlans`/`onSave`, batching over `dirtyPlans`, but that
   * batch was already dead in practice (the plan list is read-only; only the
   * OPEN plan's draft can ever be dirty) and the extracted `<PlanEditor>` has
   * no way to reach `savePlans` without re-closing the very cycle P1 exists to
   * avoid. Only the two things this screen alone can do — the stale banner's
   * state and `onGrantsChanged` — stay here.
   *
   * Mirrors the old `savePlans`'s semantics exactly: `stalePlans` is REPLACED
   * wholesale on every scalar save (not merged), even when this save had no
   * stale entitlements of its own — a save that did not affect grants clears
   * any earlier banner rather than leaving it stale-about-a-different-plan.
   */
  const handleScalarSaved = (plan: { id: string; name: string }, res: PlanScalarSaveResult) => {
    // The plan's staged scalars are saved — drop them (the `load` that follows
    // used to do this by re-seeding every draft).
    setPTouched(prev => {
      if (!(plan.id in prev)) return prev
      const { [plan.id]: _saved, ...rest } = prev
      return rest
    })
    if (res.entitlementsStale && res.entitlementsStale.tenants > 0) {
      setStalePlans({
        [plan.id]: { name: plan.name, tenants: res.entitlementsStale.tenants, fields: res.entitlementsStale.fields },
      })
    } else {
      setStalePlans({})
    }
    // A grant-affecting value changed (regardless of the undercounting live-
    // subscriber tally): offer auto-apply. The dialog computes the accurate
    // applicable set itself.
    if (res.entitlementsStale) onGrantsChanged?.(plan)
  }

  const savePacks = async () => {
    for (const id of dirtyPacks) {
      const d = kDrafts[id]
      if (!d.name.trim()) { toast.error(t('catalog.err.name', { id })); return }
      const price = parsePrice(d.priceText, d.currency)
      if (price == null || price < 1) { toast.error(t('catalog.err.packPrice', { id })); return }
      if (parseOptionalInt(d.sortText) === undefined) { toast.error(t('catalog.err.sort', { id })); return }
    }

    setSaving(true)
    // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it;
    // nothing is shown or reloaded once that session ended
    const batch = pinnedBatch()
    let done = 0
    try {
      for (const id of dirtyPacks) {
        const d = kDrafts[id]
        await batch.run(() => catalogAdminApi.updatePack(id, {
          name: d.name.trim(),
          currency: d.currency,
          priceMinor: parsePrice(d.priceText, d.currency)!,
          sortOrder: parseOptionalInt(d.sortText) ?? 0,
          isActive: d.isActive,
        }))
        done++
      }
      if (!batch.ended()) toast.success(t('catalog.toast.packsSaved', { count: done }))
    } catch (e: any) {
      if (!batch.ended()) toast.error(t('catalog.err.rowFailed', { id: dirtyPacks[done] ?? '', saved: done, error: e?.message || '' }))
    } finally {
      if (!batch.ended()) await load()
      // As the re-seeding `load` always did — on success and on a partial failure.
      clearKTouched()
      setSaving(false)
    }
  }

  /**
   * Publish credit packs to Stripe. Packs-only: billing is manual, so plans do
   * not subscribe via Stripe and are not synced from this screen (the plan-sync
   * path stays reachable via the CLI for a future provider deployment).
   * Confirmed rather than one-click: it writes to the provider, and a re-mint
   * after a price change orphans the previous Price.
   *
   * Scope follows the billing mode: under MANUAL billing plans never subscribe
   * via Stripe, so Publish is packs-only; under PROVIDER billing (the default)
   * plans DO sync, so Publish is whole-catalog — otherwise a plan price change
   * would silently never reach Stripe and checkout would refuse it.
   */
  const publish = async () => {
    const ok = await confirm({
      title: t('catalog.publish.confirmTitle'),
      message: t('catalog.publish.confirmBody'),
      type: 'warning',
      confirmText: t('catalog.publish.action'),
    })
    if (!ok) return
    setSyncing(true)
    try {
      await catalogAdminApi.sync(planAuthority === 'manual' ? { scope: 'packs' } : undefined)
      toast.success(t('catalog.toast.published'))
      await load()
    } catch (e: any) {
      toast.error(e?.message || t('catalog.err.publishFailed'))
    } finally {
      setSyncing(false)
    }
  }

  if (!isPlatformAdmin) {
    return <div className="p-4 themed-text-muted text-sm">{t('catalog.noPlatformAdmin')}</div>
  }
  if (!settings || !plans || !packs) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  // Under PROVIDER billing an unpublished plan price is a real state to fix (the
  // Stripe Price is stale/absent and checkout refuses it), so plans join the
  // banner. Under MANUAL billing plans never publish, so only packs do.
  const unpublished = [
    ...(planAuthority === 'provider'
      ? plans
          .filter((p) => {
            const price = priceFor(p, settings.billingCurrency)
            return p.isActive && !isNeverSold(price) && !price?.syncedToProvider
          })
          .map((p) => p.name)
      : []),
    ...packs.filter(k => k.isActive && !k.syncedToProvider).map(k => k.name),
  ]

  const staleList = Object.values(stalePlans)

  return (
    <div className="pb-24">
      {/* One heading for the whole price book. When the console supplies its
          own (the merged screen), this component stops competing with it —
          two `<h2>`s stacked is what "two screens on one route" looked like. */}
      {header ?? (
        <>
          <h2 className="themed-text text-lg font-medium mb-1">{t('catalog.title')}</h2>
          <p className="themed-text-muted text-sm mb-4">{t('catalog.subtitle')}</p>
        </>
      )}

      {!billingEnabled && (
        <div className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2.5 mb-4">
          <div className="text-blue-300 text-xs font-medium">{t('catalog.flagOff.title')}</div>
          <p className="themed-text-secondary text-xs mt-0.5">{t('catalog.flagOff.body')}</p>
        </div>
      )}

      {billingEnabled && unpublished.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 mb-4">
          <div className="text-amber-300 text-xs font-medium">{t('catalog.unpublished.title')}</div>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('catalog.unpublished.body', { names: unpublished.join(', ') })}
          </p>
        </div>
      )}

      {/* A4. Without this the failure is SILENT: the operator raises a plan's
          included budget, sees the new number on this page, and not one
          existing customer's cap moves. */}
      {staleList.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 mb-4">
          <div className="text-amber-300 text-xs font-medium">
            {t('catalog.stale.title', { defaultValue: 'Existing tenants are still on the old limits' })}
          </div>
          <p className="themed-text-secondary text-xs mt-0.5">
            {t('catalog.stale.body', {
              defaultValue:
                'Limits are copied onto each tenant when their subscription changes, not read from the plan on every request. {{summary}} still hold the previous values until their entitlements are re-applied.',
              summary: staleList
                .map((s) => t('catalog.stale.plan', {
                  defaultValue: '{{n}} on {{plan}}',
                  n: s.tenants,
                  plan: s.name,
                }))
                .join('; '),
            })}
          </p>
          <p className="themed-text-secondary text-xs mt-1">
            {t('catalog.stale.how', {
              defaultValue:
                'Apply it to one tenant from their Plan & billing tab, or to everyone on the plan with "Apply to tenants" on the Plans table.',
            })}
          </p>
        </div>
      )}

      {/* Metered feature pricing and per-model AI pricing are now edited PER PLAN,
          inside each plan's expanded editor — a plan defines its own prices and a
          blank inherits the deployment default. The old global rate cards (metered
          defaults + global model prices) are gone from this screen: a plan-blank
          model price falls back to the built-in default rate, and a plan-blank
          metered feature is free (the seeded deployment default). */}
      <PlansSection
        openPlanId={openPlanId}
        onOpenPlan={onOpenPlan}
        onClosePlan={onClosePlan}
        plans={plans}
        drafts={pDrafts}
        dirty={dirtyPlans}
        currencies={currencies}
        busy={busy}
        planAuthority={planAuthority}
        onDraft={(id, patch) => setPTouched(prev => withEdit(prev, id, changedFields({ ...prev[id], ...patch }, pBase[id])))}
        onScalarSaved={handleScalarSaved}
        onDiscard={clearPTouched}
        onCreated={load}
        defaultCurrency={settings.billingCurrency}
        defaultPresetId={defaultPresetId}
        onSetDefaultPreset={onSetDefaultPreset}
        onPropagate={onPropagate}
        renderPlanTenants={renderPlanTenants}
        t={t}
      />

      <PacksSection
        packs={packs}
        drafts={kDrafts}
        dirty={dirtyPacks}
        currencies={currencies}
        busy={busy}
        onDraft={(id, patch) => setKTouched(prev => withEdit(prev, id, changedFields({ ...prev[id], ...patch }, kBase[id])))}
        onSave={savePacks}
        onDiscard={clearKTouched}
        onCreated={load}
        defaultCurrency={settings.billingCurrency}
        t={t}
      />

      {/* Publish last: it is the step that makes everything above purchasable,
          and reads as the end of the flow rather than a control per section. */}
      <Section title={t('catalog.publish.title')} desc={t('catalog.publish.desc')}>
        <button
          onClick={publish}
          disabled={!billingEnabled || syncing || busy}
          title={billingEnabled ? undefined : t('catalog.publish.needsFlag')}
          className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
        >
          {syncing ? t('catalog.publish.working') : t('catalog.publish.action')}
        </button>
      </Section>

    </div>
  )
}

// ─── Shared bits ──────────────────────────────────────────────────────

function Section({
  title,
  desc,
  info,
  children,
}: {
  title: string
  desc?: string
  /** Helper text behind an info icon beside the title, instead of a visible
   *  `desc` paragraph. A call site passes one or the other. */
  info?: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-6">
      <h3 className="themed-text text-sm font-medium mb-1 flex items-center">
        {title}
        {info && <InfoHint text={info} />}
      </h3>
      {/* Capped here rather than on the page — see the shell's width note. */}
      {desc && <p className="themed-text-muted text-xs mb-2 max-w-prose">{desc}</p>}
      {children}
    </section>
  )
}

function Field({
  label,
  hint,
  value,
  onChange,
  disabled,
  width = 'w-28',
}: {
  label: string
  hint?: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  width?: string
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="themed-text-secondary text-[11px]">{label}</span>
      <input
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        inputMode="decimal"
        aria-label={label}
        className={`themed-input border rounded px-1.5 py-1 text-xs ${width} outline-none focus:border-blue-500 disabled:opacity-50`}
      />
      {hint && <span className="themed-text-muted text-[10px]">{hint}</span>}
    </label>
  )
}

function CurrencySelect({
  value,
  currencies,
  disabled,
  label,
  onChange,
}: {
  value: string
  currencies: string[]
  disabled?: boolean
  label: string
  onChange: (v: string) => void
}) {
  return (
    <select
      value={value.toUpperCase()}
      disabled={disabled}
      aria-label={label}
      onChange={e => onChange(e.target.value.toLowerCase())}
      className="themed-input border rounded px-1 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
    >
      {currencies.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  )
}

/**
 * Commit bar — one per section, so a dirty plan grid never commits an unrelated
 * half-typed settings change.
 *
 * ── Why `sticky bottom-0` is load-bearing, not decoration ────────────────
 *
 * This comment said "Sticky commit bar" for the whole life of the component
 * while the element was `position: static`. It is rendered at the END of its
 * section, and the Plans section ends AFTER the overage grid, the model matrix,
 * the uploads list, the billing-rhythm rows and the AI rate card — so editing a
 * plan's storage quota put the only control that could commit it **1,656px
 * below the field**, off-screen, with nothing near the input to say anything was
 * pending. Measured in the live DOM: `saveTop 2354, fieldTop 699, viewportH 907`.
 *
 * The operator's experience was: type a number, tab out, see nothing happen,
 * navigate away, lose the edit. Sticking the bar to the bottom of the scroll
 * container makes it visible the moment the section is dirty, wherever in that
 * section you are.
 *
 * Fixed HERE rather than at the eight call sites deliberately: five of those
 * sections are due to be folded into a per-plan editor, and a per-call-site fix
 * would be written and then deleted. A fix in the shared component survives the
 * restructure and covers every bar for free.
 */
export function SaveBar({
  count,
  busy,
  onSave,
  onDiscard,
  t,
}: {
  count: number
  busy: boolean
  onSave: () => void
  onDiscard: () => void
  t: T
}) {
  // F-33 — one bar for the whole console. This wrapper keeps the price book's
  // own pluralised copy and hands everything else to `DirtyBar`: sticky so it
  // cannot render off-screen from the field it commits (which is F-14, the one
  // defect in this review that could lose an operator's work), announced, and
  // absent while nothing is staged.
  return (
    <DirtyBar
      count={count}
      busy={busy}
      onSave={onSave}
      onDiscard={onDiscard}
      pendingLabel={t('catalog.pending', { count })}
      discardLabel={t('catalog.discard')}
      saveLabel={t('catalog.save')}
      savingLabel={t('catalog.saving')}
    />
  )
}

/** Published / not-published, per row. The badge is the only warning an admin
 *  gets before a customer meets the 409. */
function SyncBadge({ synced, t }: { synced: boolean; t: T }) {
  return synced ? (
    <span className="text-[10px] px-1 py-0.5 rounded bg-green-500/20 text-green-300 whitespace-nowrap">
      {t('catalog.published')}
    </span>
  ) : (
    <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 whitespace-nowrap">
      {t('catalog.notPublished')}
    </span>
  )
}

// ─── Deployment settings ──────────────────────────────────────────────

/**
 * The deployment's charging config — default currency, the credit markup (THE
 * GROSS MARGIN) and the self-serve purchase band.
 *
 * ── Why this is its own component now (redesign phase 5) ────────────────
 *
 * It used to be a section of the catalog screen, which is where it was
 * misfiled: it is platform CONFIGURATION, not a price, so it is `settings:write`
 * rather than `pricing:write` — the read-only `viewer`/`guest` tier holds
 * `settings:read` and may not edit it. (Before 2026-09-03 the `member` seat also
 * held `settings:read` only; member is now full write.) Phase 5 moves it to
 * Platform settings, beside the realised margin it should be set against.
 *
 * It owns its own fetch and save rather than taking them as props, because it
 * no longer shares a screen with anything that reads the same row — and a
 * self-contained component is what let `CatalogAdmin` drop the second
 * capability flag it was carrying only for this block.
 *
 * It stays in this FILE (rather than moving to `@papan/orgs` beside the rest of
 * Platform settings) so it keeps using this module's `Section`/`Field`/
 * `CurrencySelect`/`SaveBar` idiom. Re-homing it would have meant duplicating
 * four helpers to move seventy lines.
 */
export function BillingSettingsSection({ canEdit = true }: { canEdit?: boolean }) {
  const { t } = useTranslation('billing')
  const toast = useToast()
  const [currencies, setCurrencies] = useState<string[]>([])
  const [base, setBase] = useState<SettingsDraft | null>(null)
  // The staged fields over the stored settings, kept across a phone/desktop
  // layout swap (#897) — only what was touched, so the first load after a swap
  // shows through everything else and never wipes the edit. Save / Discard
  // drop it.
  const [touched, setTouched, { clear: clearTouched }] = useDraftState<Partial<SettingsDraft>>('catalog:settings', NO_EDITS)
  const draft = useMemo(() => (base ? { ...base, ...touched } : null), [base, touched])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // ── Rate-history state (0172) ────────────────────────────────────────
  const [epochs, setEpochs] = useState<RateEpochView[]>([])
  /** `''` = effective now. A date-time the browser gives us in LOCAL time. */
  const [effectiveFrom, setEffectiveFrom, { clear: clearEffectiveFrom }] = useDraftState('catalog:settings:effectiveFrom', '')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [s, hist] = await Promise.all([
        catalogAdminApi.getSettings(),
        // A `pricing:read`-gated sibling could 403 for a seat that can still see
        // the settings page, so a failure here must not blank the whole section.
        catalogAdminApi.getRateHistory().catch(() => ({ epochs: [] as RateEpochView[] })),
      ])
      setCurrencies(s.supportedCurrencies)
      setBase(settingsDraft(s.settings))
      setEpochs(hist.epochs)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // The rate and the currency are the two epoch-backed fields; everything else
  // on this form is an ordinary column patch.
  const rateChanged = !!base && !!draft
    && (draft.rateText !== base.rateText || draft.currency !== base.currency)

  const save = async () => {
    if (!draft) return
    // Major units in, minor units on the wire — see `settingsDraft`.
    const rate = parsePrice(draft.rateText, draft.currency)
    if (rate == null || rate < 1) { toast.error(t('catalog.err.rate')); return }
    const floor = parsePrice(draft.floorText, draft.currency)
    const ceiling = parsePrice(draft.ceilingText, draft.currency)
    if (floor == null || floor < 1 || ceiling == null || ceiling < 1) {
      toast.error(t('catalog.err.band')); return
    }
    if (floor > ceiling) { toast.error(t('catalog.err.bandOrder')); return }

    setSaving(true)
    try {
      const res = await catalogAdminApi.updateSettings({
        currency: draft.currency,
        creditRateMinorPerUsd: rate,
        purchaseFloorMinor: floor,
        purchaseCeilingMinor: ceiling,
        // `datetime-local` is local time with no zone; `new Date()` reads it as
        // local and `toISOString()` converts. Omitted entirely when blank, which
        // the server reads as "now".
        ...(rateChanged && effectiveFrom
          ? { effectiveFrom: new Date(effectiveFrom).toISOString() }
          : {}),
      })
      setBase(settingsDraft(res.settings))
      clearTouched()
      clearEffectiveFrom()
      if (res.epoch?.scheduled) {
        toast.success(t('catalog.toast.rateScheduled', {
          defaultValue: 'Rate change scheduled for {{when}}',
          when: new Date(res.epoch.effectiveFrom).toLocaleString(PLATFORM_DATE_LOCALE),
        }))
      } else {
        toast.success(t('catalog.toast.settingsSaved'))
      }
      const hist = await catalogAdminApi.getRateHistory().catch(() => ({ epochs }))
      setEpochs(hist.epochs)
    } catch (e: any) {
      toast.error(e?.message || t('catalog.err.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const cancelEpoch = async (id: string) => {
    try {
      await catalogAdminApi.cancelRateEpoch(id)
      setEpochs(e => e.filter(x => x.id !== id))
      toast.success(t('catalog.toast.epochCancelled', { defaultValue: 'Scheduled change cancelled' }))
    } catch (e: any) {
      toast.error(e?.message || t('catalog.err.saveFailed'))
    }
  }

  if (!draft) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  return (
    <>
      <SettingsSection
        draft={draft}
        currencies={currencies}
        dirty={!!base && !same(draft, base)}
        busy={saving || !canEdit}
        rateChanged={rateChanged}
        effectiveFrom={effectiveFrom}
        onEffectiveFrom={setEffectiveFrom}
        onDraft={patch => setTouched(d => changedFields({ ...d, ...patch }, base))}
        onSave={save}
        onDiscard={() => { clearTouched(); clearEffectiveFrom() }}
        t={t}
      />
      <RateHistory epochs={epochs} canEdit={canEdit} onCancel={cancelEpoch} t={t} />
    </>
  )
}

/**
 * The rate timeline — the audit trail `billing_settings` never had.
 *
 * Answers two different questions that used to have no answer at all: "what were
 * we charging in March" (every past epoch, with who set it) and "is anything
 * about to change" (pending rows, with a cancel).
 *
 * Cancel appears only on PENDING rows because that is the only deletion the
 * database permits — an epoch that has taken effect governed real charges. The
 * server refuses the rest with `409 EPOCH_ALREADY_EFFECTIVE`, so hiding the
 * button is an affordance rather than the enforcement.
 */
function RateHistory({
  epochs, canEdit, onCancel, t,
}: {
  epochs: RateEpochView[]
  canEdit: boolean
  onCancel: (id: string) => void
  t: T
}) {
  if (epochs.length === 0) return null
  return (
    <Section
      title={t('catalog.rateHistory.title', { defaultValue: 'Rate history' })}
      desc={t('catalog.rateHistory.desc', {
        defaultValue: 'Every rate this deployment has charged, and anything scheduled. '
          + 'Past spend is always converted at the rate that was in force when it was incurred.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="themed-text-secondary text-[11px]">
            <tr>
              <th className="px-3 py-2 text-left">{t('catalog.rateHistory.from', { defaultValue: 'In force from' })}</th>
              <th className="px-3 py-2 text-left">{t('catalog.settings.rate')}</th>
              <th className="px-3 py-2 text-left">{t('catalog.rateHistory.actor', { defaultValue: 'Set by' })}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {epochs.map(e => (
              <tr key={e.id} className="border-t themed-border">
                <td className="px-3 py-2 themed-text">
                  {/* A genesis epoch has no date because it predates history —
                      saying "unknown" would imply a lost fact rather than a
                      deliberate starting point. */}
                  {e.isGenesis
                    ? t('catalog.rateHistory.genesis', { defaultValue: 'Since the beginning' })
                    : new Date(e.effectiveFrom!).toLocaleString(PLATFORM_DATE_LOCALE)}
                  {e.pending && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                      {t('catalog.rateHistory.pending', { defaultValue: 'Scheduled' })}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 themed-text tabular-nums">
                  {formatCurrencyMinor(e.creditRateMinorPerUsd, e.currency)}
                  <span className="themed-text-muted"> / US$1.00</span>
                </td>
                <td className="px-3 py-2 themed-text-secondary text-xs">{e.actorEmail ?? '—'}</td>
                <td className="px-3 py-2 text-right">
                  {e.pending && canEdit && (
                    <button
                      type="button"
                      onClick={() => onCancel(e.id)}
                      className="text-xs px-2 py-1 rounded themed-btn-secondary"
                    >
                      {t('catalog.rateHistory.cancel', { defaultValue: 'Cancel' })}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  )
}

function SettingsSection({
  draft,
  currencies,
  dirty,
  busy,
  rateChanged,
  effectiveFrom,
  onEffectiveFrom,
  onDraft,
  onSave,
  onDiscard,
  t,
}: {
  draft: SettingsDraft
  currencies: string[]
  dirty: boolean
  busy: boolean
  /** The rate or currency moved — the two epoch-backed fields. */
  rateChanged: boolean
  effectiveFrom: string
  onEffectiveFrom: (v: string) => void
  onDraft: (patch: Partial<SettingsDraft>) => void
  onSave: () => void
  onDiscard: () => void
  t: T
}) {
  const rate = parsePrice(draft.rateText, draft.currency)
  const rateValid = rate != null && rate > 0
  // `datetime-local` needs a local-time string with no zone and no seconds.
  const minLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
    .toISOString().slice(0, 16)

  return (
    <Section title={t('catalog.settings.title')} desc={t('catalog.settings.desc')}>
      <div className="themed-bg-secondary rounded-lg p-3 flex flex-wrap items-start gap-4">
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-[11px]">{t('catalog.settings.currency')}</span>
          <CurrencySelect
            value={draft.currency}
            currencies={currencies}
            disabled={busy}
            label={t('catalog.settings.currency')}
            onChange={currency => onDraft({ currency })}
          />
          <span className="themed-text-muted text-[10px]">{t('catalog.settings.currencyHint')}</span>
        </label>

        <Field
          label={t('catalog.settings.rate')}
          // Rule 2: "550" reads like an FX rate. Render what it actually means
          // — the price of one dollar of credit — right under the field.
          hint={rateValid
            ? t('catalog.settings.rateMeans', {
                price: formatCurrencyMinor(rate!, draft.currency),
              })
            : t('catalog.err.rate')}
          value={draft.rateText}
          onChange={rateText => onDraft({ rateText })}
          disabled={busy}
          width="w-24"
        />

        <Field
          label={t('catalog.settings.floor')}
          value={draft.floorText}
          onChange={floorText => onDraft({ floorText })}
          disabled={busy}
          width="w-24"
        />
        <Field
          label={t('catalog.settings.ceiling')}
          hint={t('catalog.settings.bandHint')}
          value={draft.ceilingText}
          onChange={ceilingText => onDraft({ ceilingText })}
          disabled={busy}
          width="w-24"
        />
      </div>

      {rateChanged && (
        <div className="themed-bg-secondary rounded-lg p-3 mt-2 flex flex-col gap-3">
          {/* ── When ─────────────────────────────────────────────────── */}
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-[11px]">
              {t('catalog.settings.effectiveFrom', { defaultValue: 'Takes effect' })}
            </span>
            <input
              type="datetime-local"
              className="themed-input text-sm px-2 py-1 rounded w-56"
              value={effectiveFrom}
              // The past is refused by the route AND by a database trigger — a
              // backdated epoch would silently restate history, which is the
              // whole defect this model removes. `min` is the affordance.
              min={minLocal}
              disabled={busy}
              onChange={e => onEffectiveFrom(e.target.value)}
            />
            <span className="themed-text-muted text-[10px]">
              {effectiveFrom
                ? t('catalog.settings.effectiveLater', {
                    defaultValue: 'Scheduled. Nothing changes until then — spend before it keeps the current rate.',
                  })
                : t('catalog.settings.effectiveNow', {
                    defaultValue: 'Leave blank to take effect immediately. Past months are never restated.',
                  })}
            </span>
          </label>

          {/* The pack-review control is GONE (0177). A pack now grants its
              own FACE VALUE — buy RM50, receive RM50 — so a rate change cannot
              restate what any pack grants and there is no decision to force.
              It asked the operator to choose between two identical outcomes,
              which is worse than asking nothing: it teaches them to click
              through a prompt that once mattered. */}
        </div>
      )}

      <SaveBar count={dirty ? 1 : 0} busy={busy} onSave={onSave} onDiscard={onDiscard} t={t} />
    </Section>
  )
}

// ─── Plans ────────────────────────────────────────────────────────────

/**
 * The plan editor's "Tenant-specific controls" section (redesign).
 *
 * Collapsed: the count of tenants on this plan + a "Default" chip when it is the
 * starting plan for new tenants. Expanded: set-as-default and apply-to-tenants
 * (the latter opens the previewed propagation dialog, which itself lists the
 * linked tenants bucketed by whether they hold overrides). Opens pre-expanded
 * when reached from a row's Tenants count.
 */
export function PlanTenantControls({
  plan, planName, isDefault, canSetDefault, defaultExpanded, busy,
  onSetDefault, onPropagate, renderPlanTenants, t,
}: {
  plan: AdminPlanView
  planName: string
  isDefault: boolean
  canSetDefault: boolean
  defaultExpanded: boolean
  busy: boolean
  onSetDefault?: (planId: string) => Promise<void>
  onPropagate?: (plan: { id: string; name: string }) => void
  renderPlanTenants?: (plan: { id: string; name: string }) => ReactNode
  t: T
}) {
  const [open, setOpen] = useState(defaultExpanded)
  return (
    <div className="mt-4 border-t themed-border pt-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex items-center gap-2 w-full text-left"
      >
        <span className="themed-text text-sm font-medium">
          {t('catalog.plans.tenantControls', { defaultValue: 'Tenant-specific controls' })}
        </span>
        <span className="themed-text-muted text-xs">
          {t('catalog.plans.tenantCount', { defaultValue: '{{count}} tenants', count: plan.assignedTenantCount })}
        </span>
        {isDefault && (
          <span
            title={t('catalog.plans.seedsNew', { defaultValue: 'New tenants start here' })}
            className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300"
          >
            {t('catalog.plans.seedsNewShort', { defaultValue: 'Starting plan' })}
          </span>
        )}
        <span className="ml-auto themed-text-muted text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {onSetDefault && (
            <button
              onClick={() => { void onSetDefault(plan.id) }}
              disabled={busy || isDefault || !canSetDefault}
              title={isDefault
                ? t('catalog.plans.alreadyDefault', { defaultValue: 'Already the default for new tenants' })
                : undefined}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
            >
              {isDefault
                ? t('catalog.plans.isDefault', { defaultValue: 'Default for new tenants' })
                : t('catalog.plans.setDefault', { defaultValue: 'Set as default for new tenants' })}
            </button>
          )}
          {onPropagate && (
            <button
              onClick={() => onPropagate({ id: plan.id, name: planName })}
              disabled={busy}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
            >
              {t('catalog.plans.applyToTenants', { defaultValue: 'Apply to tenants' })}
            </button>
          )}
        </div>
      )}
      {/* The linked-tenant list + "Link tenants" control (host slot). Rendered
          only while expanded so its fetch runs on demand, not on every modal
          open. */}
      {open && renderPlanTenants && (
        <div className="mt-3">{renderPlanTenants({ id: plan.id, name: planName })}</div>
      )}
    </div>
  )
}

export function PlansSection({
  openPlanId,
  onOpenPlan,
  onClosePlan,
  plans,
  drafts,
  dirty,
  currencies,
  busy,
  planAuthority,
  onDraft,
  onScalarSaved,
  onDiscard,
  onCreated,
  defaultCurrency,
  defaultPresetId,
  onSetDefaultPreset,
  onPropagate,
  renderPlanTenants,
  t,
}: {
  plans: AdminPlanView[]
  drafts: Record<string, PlanDraft>
  dirty: string[]
  currencies: string[]
  busy: boolean
  /** 'manual' hides the extra-seat provider fields in the expanded editor. */
  planAuthority: 'provider' | 'manual'
  onDraft: (id: string, patch: Partial<PlanDraft>) => void
  /** Fired after `<PlanEditor>`'s scalar section saves — bubbles the plan
   *  identity + `catalogAdminApi.updatePlan`'s stale-entitlements report up to
   *  `CatalogAdmin`'s banner state (`handleScalarSaved`), which this screen
   *  cannot own itself (it renders per-modal, not per-page). */
  onScalarSaved: (plan: { id: string; name: string }, res: PlanScalarSaveResult) => void
  /** Also doubles as the scalar section's own discard — see `onDiscardScalars`
   *  passed to `<PlanEditor>` below. */
  onDiscard: () => void
  onCreated: () => Promise<void>
  /** From the URL — see `CatalogAdmin`'s prop of the same name. */
  openPlanId?: string
  onOpenPlan?: (planId: string) => void
  onClosePlan?: () => void
  defaultCurrency: string
  defaultPresetId?: string | null
  onSetDefaultPreset?: (planId: string) => Promise<void>
  onPropagate?: (plan: { id: string; name: string }) => void
  renderPlanTenants?: (plan: { id: string; name: string }) => ReactNode
  t: T
}) {
  // `undefined` = the host did not wire the pointer, so the column is absent.
  // `null` is a real, different state: no default configured, which the notice
  // under the table names rather than leaving as an empty column.
  const showsDefault = defaultPresetId !== undefined
  const toast = useToast()
  const [copying, setCopying] = useState<string | null>(null)
  // Which plan (if any) should open with its Tenant-specific controls section
  // pre-expanded — set when the modal is opened from a row's Tenants count.
  const [expandTenantsPlan, setExpandTenantsPlan] = useState<string | null>(null)
  /**
   * Which plan's editor is open (F-4 hybrid).
   *
   * ── Why an expander and not a route ──────────────────────────────────
   *
   * The table used to carry TWELVE columns and was 1,248px wide inside an 828px
   * column, so it scrolled horizontally — and the three controls past the right
   * edge were "Set as default", "Apply to tenants" (which rewrites live tenants'
   * ceilings, with no undo) and "Duplicate". Worse, by the time you had scrolled
   * to them the NAME column had scrolled out, so every row showed an identical
   * strip of buttons with nothing saying which plan they belonged to.
   *
   * The columns that stayed are the ones worth COMPARING across plans. Everything
   * that describes one plan in depth — concurrency, sort order, overage, models,
   * uploads, billing rhythm — moved into this expander, which is what turns "what
   * does Default actually give a tenant?" from six lookups across two screens of
   * scroll into one click.
   *
   * Local state rather than a URL segment: a plan editor is not a thing anyone
   * pastes into a ticket (the TENANT is — see CustomerDetail), and a route would
   * mean touching `billing-route-depth.test.ts` for no reader benefit.
   */
  // Derived from the URL, never held here. `undefined` (no id in the path)
  // means the list; a stale id that no longer names a plan renders the list
  // too, rather than an empty pane — see the lookup below.
  const openPlan = openPlanId ?? null
  const setOpenPlan = (id: string | null) => {
    if (id) onOpenPlan?.(id)
    else { onClosePlan?.(); setExpandTenantsPlan(null) }
  }
  /** Open a plan's editor with the Tenant-specific controls pre-expanded. */
  const openPlanTenants = (id: string) => { setExpandTenantsPlan(id); onOpenPlan?.(id) }

  /**
   * Whether the "+ Add a plan" draft modal is open (P2). Local, not the URL —
   * same reasoning as `openPlan` above, and it is deliberately its OWN piece
   * of state rather than a sentinel value of `openPlanId`: a draft has no
   * server-minted id to route on, and keeping it separate is what makes the
   * two modals trivially mutually exclusive to render (`creating ? ... :
   * openPlan ? ... : null`) without inventing a magic id string a real plan
   * could someday collide with.
   *
   * `confirmDraft`'s `onOpenPlan` (below) closes this BEFORE calling the
   * host's `onOpenPlan`, so the create modal unmounts in the same tick the
   * real (now-persisted) plan's edit modal is asked to open — the "close the
   * dup-risk window" fix Task 2's review called for: the draft's
   * `createdIdRef` guard against a second Confirm click only matters while
   * the draft form is still mounted.
   */
  // Kept across a phone/desktop layout swap (#897) with the new plan's draft
  // inside it (`PlanEditor`'s `planCreate:new`), so the swap doesn't close it.
  const [creating, setCreating, { clear: clearCreating }] = useDraftState('catalog:creatingPlan', false)
  /**
   * Mirrors `<PlanEditor>`'s own `confirming` in-flight flag (via
   * `onConfirmingChange`) — fix-round: `dismissible={!busy}` below only knew
   * this screen's OWN `busy`, not whether Confirm's create→price→activate
   * sequence was actually running, so Escape/backdrop during Confirm
   * unmounted the dialog while that promise chain kept going to completion in
   * the background — the plan still got created, priced and activated after
   * the operator had explicitly asked to back out. `Modal`'s own docblock:
   * `dismissible={false}` is exactly the contract for a write in flight.
   */
  const [creatingBusy, setCreatingBusy] = useState(false)
  // #897 — a create Confirmed on the screen a layout swap replaced finishes
  // there: its `onOpenPlan` still closes this form (key-level clears) and
  // navigates to the plan (the router outlives the screens), but its reload
  // refreshed the OLD list, so the plan it opens is missing from this one.
  // Reload here — and only that: navigating again would push a second history
  // entry, or pull the operator back if they moved on meanwhile.
  useDraftInFlight<string | null>('planCreate:new', (o) => {
    if (!o.ok || !o.value) return
    void onCreated()
  })
  // The create modal's OWN Save/Discard controller — `<PlanEditor>` calls
  // `useRegisterSection(bar, 'scalar', ...)` unconditionally regardless of
  // `scope.kind` (rules-of-hooks, see that component's docblock), so a real
  // `PlanEditBar` is required even though `plan-create` never shows the
  // aggregate `SaveBar` this drives — one Confirm button replaces it. A
  // fresh instance (not the edit dialog's `bar`) so the two modals' section
  // registrations can never collide.
  const { bar: createBar } = usePlanEditBar()

  // ONE Save/Discard for the whole Edit dialog. Every editable section registers
  // into this controller and hides its own bar; the aggregate below fans out.
  const { bar, total: barTotal, saveAll, discardAll } = usePlanEditBar(['scalar'])
  const [barSaving, setBarSaving] = useState(false)
  // The scalar fields (name, seats, storage, concurrency, active) used to be
  // registered here via `useRegisterSection(openPlan ? bar : undefined,
  // 'scalar', ..., () => onSave({silent:true}), onDiscard)`, driving a
  // multi-row `savePlans` batch. P1 Task 3 moved the registration INTO
  // `<PlanEditor>` itself, next to `savers` — that dialog-scoped bar is the
  // one place both the injected saver and this plan's own draft are already
  // in scope, and `<PlanEditor>` naturally registers-on-mount /
  // unregisters-on-unmount exactly when `openPlan` toggles, matching the old
  // conditional-bar behaviour without re-deriving it here. `onScalarSaved`
  // below is what still needs to happen at THIS level (the stale banner is
  // page state, not dialog state).
  const runBarSave = async () => {
    setBarSaving(true)
    // #1316 review round 2: ONE batch for the Save — every section writes as the identity that pressed it
    const batch = pinnedBatch()
    try {
      await saveAll(batch)
      if (!batch.ended()) toast.success(t('catalog.plans.savedAll', { defaultValue: 'Changes saved.' }))
    } catch (e: any) {
      // A section can HALT the batch without it being an error — the operator
      // cancelled a mid-batch confirm (e.g. the billing-rhythm reprice). No
      // green "saved" (the throw skipped it) and no red error either.
      if (e?.name !== 'BarAbort' && !batch.ended()) toast.error(e?.message || t('catalog.err.saveFailed'))
    } finally {
      setBarSaving(false)
    }
  }

  // A plan is presented by what it GIVES — free credits, included seats, storage
  // — not by a per-seat price (which is a provider concept, edited in the expander
  // and hidden under manual billing). The old `price` column moved into the editor.
  //
  // AUDIENCE (personal / organization / both) is hidden under manual billing.
  // Its only behavioural effect is on the customer-facing plan PICKER and the
  // provider upsell path — both dormant under manual — plus propagation's
  // audience-match. On a deployment that runs one tenant type (the Gnexis case:
  // organization-only, manual), the dropdown distinguishes nothing and only adds
  // a decision for the operator. The column and its guards stay intact
  // server-side; new plans default to `'both'` (matches any type), so hiding the
  // control never wrongly narrows a plan. It returns with provider billing, when
  // the personal-vs-organization plan model is settled. Same stance as the
  // extra-seat fields below. `showAudience` itself now lives in `PlanEditor.tsx`
  // (the dialog it gates), moved there with the rest of the modal body.
  //
  // The list is READ-ONLY key details now; all editing happens in the Edit
  // dialog. Audience moved into the dialog (it was already hidden under manual).
  // 'status' removed (redesign): the Retired/publish/starting-plan chips it
  // carried are either irrelevant under the default manual authority (sync
  // badge) or relocated (the "Default" chip now sits in the modal's
  // Tenant-specific controls). The Active toggle is gone from the modal too.
  const cols = ['name', 'freeCredits', 'includedSeats', 'storage', 'uploads', 'models', 'tenants', 'actions']

  /**
   * ── THE OFF-CURRENCY WARNING IS GONE, BECAUSE THE STATE IS ─────────────
   *
   * It warned when a plan's own `currency` column diverged from the
   * deployment's: a per-row select with 27 options, one mis-click, and the cost
   * landing somewhere else entirely (Overview's realised margin sums invoices
   * in the billing currency and silently drops the rest).
   *
   * A plan has no currency column any more. It is priced PER currency and this
   * editor edits the deployment's own, so there is no divergence left to warn
   * about — a plan sold in another market is an extra ROW rather than a plan
   * pointing somewhere else, and it neither hides revenue nor changes what
   * anyone here is charged.
   */

  return (
    <Section title={t('catalog.plans.title')} desc={t('catalog.plans.desc')}>
      <div className="themed-bg-secondary rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b themed-border">
              {/* NOT `whitespace-nowrap`. The header text is what sets the
                  column widths here, and two long ones ("Included budget /
                  seat (US$)", "Storage quota (GB)") were together pushing the
                  table 142px past its container — reintroducing the horizontal
                  scroll this restructure exists to remove. Letting them wrap to
                  two lines costs a few pixels of height once. */}
              {cols.map(col => (
                <th
                  key={col}
                  // The NAME column is pinned, like the feature matrix's first
                  // cell. Any horizontal scrolling that survives the width work
                  // then stays legible: without it, scrolling right to reach
                  // Status or Edit loses which plan the row is.
                  className={`text-left px-2 py-2 themed-text-secondary text-xs font-medium align-bottom${
                    col === 'name' ? ' sticky left-0 z-10 themed-bg-secondary' : ''
                  }`}
                >
                  {t(`catalog.plans.col.${col}`, {
                    defaultValue: {
                      actions: '', uploads: 'Uploads', models: 'AI models', tenants: 'Tenants',
                    }[col],
                  })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plans.map(p => {
              const d = drafts[p.id]
              if (!d) return null
              // Which rows the Save bar is about to commit. The bar states a
              // COUNT ("2 unsaved"), which on a multi-row grid does not say
              // WHICH two — and the plan name can be scrolled out of view
              // horizontally while you are editing a column on the right.
              const isDirty = dirty.includes(p.id)
              /** This plan's money in the currency the deployment charges in.
               *  `null` = unpriced, which the badge reports as unpublished. */
              const rowPrice = priceFor(p, defaultCurrency)
              const modelsList = p.features?.models as string[] | undefined
              const uploadsPaused = planBlocksCreate(p.features)
              return (
                <tr
                  key={p.id}
                  className={`border-b themed-border last:border-b-0 ${
                    isDirty ? 'bg-blue-500/10' : ''
                  }`}
                >
                  {/* Name and id in ONE cell. The id is the API handle and stays
                      legitimately useful, but it does not deserve a column of its
                      own — and merging them is what keeps the identifying cell
                      first and always visible. */}
                  {/* Pinned to match the header cell — the row's identity has
                      to survive any horizontal scroll that remains. The
                      background is explicit because a sticky cell scrolls OVER
                      its neighbours and a transparent one would show them
                      through. */}
                  {/* The list is READ-ONLY key details; editing is in the dialog.
                      Values come from the SAVED plan (`p`), not the draft. */}
                  <td className="px-2 py-2 sticky left-0 z-10 themed-bg-secondary">
                    <div className="flex items-center gap-1.5">
                      {isDirty && (
                        <span
                          aria-label={t('catalog.unsavedRow', { defaultValue: 'Unsaved changes' })}
                          title={t('catalog.unsavedRow', { defaultValue: 'Unsaved changes' })}
                          className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-blue-400"
                        />
                      )}
                      <span className="themed-text text-sm font-medium">{p.name}</span>
                    </div>
                    <div className="themed-text-muted font-mono text-[10px] mt-0.5 ml-0.5">{p.id}</div>
                  </td>
                  {/* FREE CREDITS — base_pool_billing_micro in the plan's own
                      currency. Unpriced is a refusing state, not a free one. */}
                  <td className="px-2 py-2 whitespace-nowrap themed-text text-xs">
                    {rowPrice
                      ? formatBillingMicro(rowPrice.basePoolBillingMicro, rowPrice.currency)
                      : <span className="themed-text-muted">{t('catalog.plans.unpriced', { defaultValue: 'Unpriced' })}</span>}
                  </td>
                  <td className="px-2 py-2 themed-text text-xs">{p.includedSeats}</td>
                  <td className="px-2 py-2 whitespace-nowrap themed-text text-xs">
                    {formatStorage(p.storageQuotaBytes) ?? '—'}
                  </td>
                  {/* UPLOADS — allowed / paused, read-only chip. */}
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      uploadsPaused ? 'bg-amber-500/20 text-amber-300' : 'themed-text-muted'
                    }`}>
                      {uploadsPaused
                        ? t('catalog.plans.uploads.paused', { defaultValue: 'Uploads paused' })
                        : t('catalog.plans.uploads.allowed', { defaultValue: 'Uploads allowed' })}
                    </span>
                  </td>
                  {/* AI MODELS — how many are allowed. Absent list = all. */}
                  <td className="px-2 py-2 whitespace-nowrap themed-text-muted text-xs">
                    {modelsList && modelsList.length > 0
                      ? t('catalog.plans.modelsCount', { defaultValue: '{{count}} allowed', count: modelsList.length })
                      : t('catalog.plans.modelsAll', { defaultValue: 'All' })}
                  </td>
                  {/* TENANTS — explicitly assigned this plan (override + live subs).
                      Clicking opens the editor with the Tenant-specific controls
                      pre-expanded. A "Default" chip (the starting plan for new
                      tenants) rides alongside, relocated from the removed Status
                      column. */}
                  <td className="px-2 py-2 whitespace-nowrap text-xs">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => openPlanTenants(p.id)}
                        aria-haspopup="dialog"
                        className="themed-text-muted underline-offset-2 hover:underline hover:themed-text"
                        title={t('catalog.plans.openTenants', { defaultValue: 'Manage tenants on this plan' })}
                      >
                        {p.assignedTenantCount}
                      </button>
                      {showsDefault && defaultPresetId === p.id && (
                        <span
                          title={t('catalog.plans.seedsNew', { defaultValue: 'New tenants start here' })}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 whitespace-nowrap"
                        >
                          {t('catalog.plans.seedsNewShort', { defaultValue: 'Starting plan' })}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-2 py-2 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setOpenPlan(p.id)}
                        aria-haspopup="dialog"
                        className="text-[11px] px-2 py-1 rounded themed-btn-secondary"
                      >
                        {t('catalog.plans.edit', { defaultValue: 'Edit' })}
                      </button>
                      <PlanRowMenu
                        plan={p}
                        busy={busy || copying === p.id}
                        copying={copying === p.id}
                        isDefault={showsDefault && defaultPresetId === p.id}
                        canSetDefault={showsDefault}
                        pausesUploads={pausesUploads(p)}
                        onSetDefault={onSetDefaultPreset}
                        onPropagate={onPropagate}
                        onDuplicate={async () => {
                          setCopying(p.id)
                          try {
                            const id = await duplicatePlan(p, plans.map(x => x.id))
                            await onCreated()
                            toast.success(t('catalog.plans.duplicated', {
                              defaultValue: 'Created "{{id}}", retired. Rename it and switch it on when it is ready.',
                              id,
                            }))
                          } catch (e: any) {
                            toast.error(e?.message || t('catalog.err.saveFailed'))
                          } finally {
                            setCopying(null)
                          }
                        }}
                        t={t}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* The per-plan editor, OUTSIDE the table.
          ────────────────────────────────────────────────────────────────
          Not a `<tr>` with a full-width `<td>`: these sub-editors render their
          own tables (models × plan, overage, rhythm), and a table nested inside
          a cell of the table it is describing is both a layout trap and a
          screen-reader one. Rendering it as a sibling keeps each a normal
          top-level table.

          `key` on the plan id so switching plans REMOUNTS the sub-editors —
          each holds its own draft state keyed by plan, and reusing the instance
          would carry a half-typed overage rate from one plan onto another. */}
      {openPlan && (() => {
        const p = plans.find(x => x.id === openPlan)
        if (!p) return null
        const d = drafts[p.id]
        const expandTenants = expandTenantsPlan === p.id
        return (
          <Modal
            title={d?.name || p.name}
            onClose={() => setOpenPlan(null)}
            dismissible={!busy}
            maxWidth="max-w-3xl"
          >
            {/* The modal BODY is `<PlanEditor>` (P1 Task 2 of the PlanEditor
                extraction) — everything from the scalar fields through the
                aggregate Save bar. This shell only owns open/close/URL wiring
                (`openPlanId` / `onOpenPlan` / `onClosePlan`, above) and the
                `usePlanEditBar` controller `bar`/`barTotal`/`saveAll`/`discardAll`
                come from, which is unchanged by the extraction. */}
            <PlanEditor
              scope={{ kind: 'plan', plan: p }}
              savers={planSavers(p)}
              bar={bar}
              busy={busy}
              planAuthority={planAuthority}
              currencies={currencies}
              defaultCurrency={defaultCurrency}
              draft={d}
              onDraftChange={patch => onDraft(p.id, patch)}
              onSaved={onCreated}
              onDiscardScalars={onDiscard}
              onScalarSaved={res => onScalarSaved({ id: p.id, name: d?.name?.trim() || p.name }, res)}
              defaultPresetId={defaultPresetId}
              onSetDefaultPreset={onSetDefaultPreset}
              onPropagate={onPropagate}
              renderPlanTenants={renderPlanTenants}
              expandTenants={expandTenants}
              saveBarTotal={barTotal}
              saveBarBusy={barSaving}
              onSaveAll={runBarSave}
              onDiscardAll={discardAll}
              t={t}
            />
          </Modal>
        )
      })()}

      {/* An unset pointer is not an empty column, it is provisioning falling back
          to `free` — usually a zero AI budget. Named here rather than left to be
          discovered on a new customer's first agent turn. */}
      {showsDefault && defaultPresetId === null && (
        <p className="text-amber-300/90 text-[11px] mt-1">
          {t('catalog.plans.noDefaultPreset', {
            defaultValue:
              'No preset is set as the default, so tenants created from the console are seeded from the free plan. Set one below or in Platform settings.',
          })}
        </p>
      )}

      {/* "+ Add a plan" sits directly under the grid it belongs to and opens
          the SAME `<PlanEditor>` the Edit dialog uses, in `plan-create` scope
          (P2) — a blank draft in the identical Modal shell, rather than the
          old bespoke `NewPlanForm` fields. Confirm (inside `<PlanEditor>`)
          creates the row inactive, prices it, applies any staged
          uploads/rate-limits, then activates last — see that component's
          `confirmDraft`. */}
      <button
        onClick={() => setCreating(true)}
        disabled={busy}
        className="mt-2 text-xs themed-text-muted hover:themed-text underline underline-offset-2 disabled:opacity-50"
      >
        + {t('catalog.plans.add')}
      </button>

      {/* `!openPlan` makes the mutual exclusivity with the Edit modal a
          rendering guarantee rather than something that merely happens to
          hold because the trigger button sits behind an open Modal's `inert`
          background (real browsers enforce that; a stale/racy `openPlanId`
          from the URL would not go through the button at all). */}
      {creating && !openPlan && (
        <Modal
          title={t('catalog.plans.addTitle', { defaultValue: 'New plan' })}
          description={t('catalog.plans.addDesc', {
            defaultValue:
              'Name it and set its price and free credits. Everything else starts on the platform defaults — refine uploads, billing rhythm, request limits, models and per-currency prices in the editor that opens next.',
          })}
          // Guarded on `creatingBusy` even though `Modal`'s own `close()`
          // already skips calling this when `dismissible` is false — belt and
          // braces against anything that might invoke `onClose` directly.
          onClose={() => { if (!creatingBusy) { clearCreating(); clearPlanCreateDrafts() } }}
          dismissible={!busy && !creatingBusy}
          maxWidth="max-w-3xl"
        >
          <PlanEditor
            scope={{ kind: 'plan-create', defaults: resolvePlanFieldDefaults() }}
            savers={noopPlanSavers}
            bar={createBar}
            busy={busy}
            planAuthority={planAuthority}
            currencies={currencies}
            defaultCurrency={defaultCurrency}
            draft={undefined}
            onDraftChange={() => {}}
            onSaved={onCreated}
            // `max(existing) + 10`, not `+1`: leaving gaps means a plan can be
            // slotted between two others later by editing ONE row, instead of
            // renumbering the catalogue — the same reasoning the old
            // `NewPlanForm` stated for this exact computation.
            nextSortOrder={plans.reduce((m, x) => Math.max(m, x.sortOrder), 0) + 10}
            onConfirmingChange={setCreatingBusy}
            onOpenPlan={(id) => {
              // Close the DRAFT modal and open the real plan's Edit modal on
              // the same id — mutually exclusive by construction (`creating`'s
              // own docblock above). Closing here is also what shuts the
              // dup-click window Task 2's review flagged: `confirmDraft`'s
              // `createdIdRef` guard against a second Confirm click only
              // matters while this form is still mounted, and unmounting it
              // is the actual fix.
              clearCreating()
              clearPlanCreateDrafts()
              onOpenPlan?.(id)
            }}
            saveBarTotal={0}
            saveBarBusy={false}
            onSaveAll={() => {}}
            onDiscardAll={() => {}}
            t={t}
          />
        </Modal>
      )}
    </Section>
  )
}

/** A plan whose `features.canCreateContent` is explicitly false pauses uploads.
 *  Read as `=== false`, never `!features.canCreateContent`: every plan row
 *  predates the key, so the negation form reports every plan as freezing. */
function pausesUploads(p: AdminPlanView): boolean {
  const f = (p as { features?: Record<string, unknown> }).features
  return !!f && f.canCreateContent === false
}

/**
 * The row's three consequential actions, in one menu.
 *
 * They were three columns past the right edge of a horizontally-scrolling table,
 * and reaching them scrolled the plan NAME out of view — so an operator chose
 * "Apply to tenants", which rewrites live tenants' ceilings with no undo, on a
 * row they could no longer identify. A menu anchored to a cell that is always
 * visible fixes the identification problem and the discovery problem together.
 */
export function PlanRowMenu({
  plan, busy, copying, isDefault, canSetDefault, pausesUploads: pauses,
  onSetDefault, onPropagate, onDuplicate, t,
}: {
  plan: AdminPlanView
  busy: boolean
  copying: boolean
  isDefault: boolean
  canSetDefault: boolean
  /** Seeding new tenants from a plan that freezes uploads is almost certainly a
   *  mistake, and nothing used to say so — see the confirm below. */
  pausesUploads: boolean
  onSetDefault?: (planId: string) => Promise<void>
  onPropagate?: (plan: { id: string; name: string }) => void
  onDuplicate: () => Promise<void>
  t: T
}) {
  const [open, setOpen] = useState(false)
  const confirm = useConfirm()
  const toast = useToast()
  const ref = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // ── PORTALED, and it has to be ──────────────────────────────────────
  //
  // This menu used to be `absolute` inside the row. Its ancestor is the table's
  // `overflow-x-auto` box, and setting ONE axis to `auto` forces the other from
  // `visible` to `auto` — so that box clips in BOTH directions and no
  // absolutely-positioned child can escape it. Measured on the last row: the
  // container's scrollHeight grew 226 → 307 while clientHeight stayed 226, and
  // the menu's bottom landed 73px past the edge. The items were reachable only
  // by scrolling the table itself.
  //
  // Raising z-index does nothing: clipping is not a stacking problem. A portal
  // (or `position: fixed` with a measured rect) is the only thing that escapes,
  // which is what `FeaturePricingAdmin`'s tenant search already does.
  //
  // Every table on these pages is wrapped in `overflow-x-auto` — the price book
  // alone has four — so ANY popover anchored inside one needs this treatment.
  const { getTheme } = useTheme()
  // A portal renders OUTSIDE the ThemedArea tree, where the themed-* CSS
  // variables are not defined. Without this class the menu renders unstyled.
  const themeClass = getThemeClass(getTheme('workspace'))
  const [rect, setRect] = useState<
    { left: number; width: number; maxHeight: number; top?: number; bottom?: number } | null
  >(null)

  useEffect(() => {
    if (!open) { setRect(null); return }
    // Anchor to the trigger, keep it anchored across scroll/resize, and flip
    // above rather than run off the bottom — the last row is precisely where
    // this menu is opened and precisely where there is no room below it.
    const place = () => {
      const el = ref.current
      if (!el) return
      const r = el.getBoundingClientRect()
      const GAP = 4
      const MARGIN = 8
      const vw = window.innerWidth
      const vh = window.innerHeight
      const width = 224 // w-56
      const spaceBelow = vh - r.bottom - GAP - MARGIN
      const spaceAbove = r.top - GAP - MARGIN
      const below = spaceBelow >= spaceAbove
      const maxHeight = Math.max(96, Math.floor(below ? spaceBelow : spaceAbove))
      // Right-aligned to the trigger, then clamped so it cannot leave the
      // viewport on either side.
      const left = Math.min(Math.max(r.right - width, MARGIN), vw - width - MARGIN)
      setRect(
        below
          ? { left, width, maxHeight, top: r.bottom + GAP }
          : { left, width, maxHeight, bottom: vh - r.top + GAP },
      )
    }
    place()
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      // The menu is no longer inside `ref`, so it needs its own containment
      // check — without it, clicking any menu item dismisses before the click
      // lands.
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const item = 'block w-full text-left px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary disabled:opacity-40 disabled:cursor-default'

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('catalog.plans.moreActions', { defaultValue: 'More actions for {{name}}', name: plan.name })}
        className="px-2 py-1 rounded themed-btn-secondary text-xs disabled:opacity-50"
      >
        ⋯
      </button>
      {open && rect && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className={`${themeClass} fixed z-50 w-56 overflow-y-auto rounded-lg border themed-border themed-bg-primary py-1 shadow-lg`}
          style={{
            top: rect.top,
            bottom: rect.bottom,
            left: rect.left,
            maxHeight: rect.maxHeight,
          }}
        >
          {canSetDefault && (
            <button
              role="menuitem"
              className={item}
              disabled={isDefault || !onSetDefault}
              onClick={async () => {
                setOpen(false)
                // The one guard that did not exist: nothing stopped an operator
                // seeding every new tenant from the plan whose entire purpose is
                // to freeze them.
                if (pauses) {
                  const ok = await confirm({
                    title: t('catalog.plans.defaultPausesTitle', {
                      defaultValue: 'Seed new tenants from a plan that pauses uploads?',
                    }),
                    message: t('catalog.plans.defaultPausesBody', {
                      defaultValue:
                        '“{{name}}” blocks new files and folders. Every tenant created from the console would start unable to upload anything. This is normally the non-payment fallback, not the starting plan.',
                      name: plan.name,
                    }),
                    confirmText: t('catalog.plans.defaultPausesOk', { defaultValue: 'Set it anyway' }),
                  })
                  if (!ok) return
                }
                try {
                  await onSetDefault?.(plan.id)
                } catch (e: any) {
                  toast.error(e?.message || t('catalog.err.saveFailed'))
                }
              }}
            >
              {isDefault
                ? t('catalog.plans.alreadyDefault', { defaultValue: 'New tenants already start here' })
                : t('catalog.plans.setDefault', { defaultValue: 'New tenants start here' })}
            </button>
          )}
          {onPropagate && (
            <button
              role="menuitem"
              className={item}
              onClick={() => { setOpen(false); onPropagate({ id: plan.id, name: plan.name }) }}
              title={t('catalog.plans.propagateHint', {
                defaultValue:
                  'Copy this preset\'s ceilings and model list onto the tenants already on it. Shows who first — there is no undo.',
              })}
            >
              {t('catalog.plans.propagate', { defaultValue: 'Apply to tenants' })}
            </button>
          )}
          <button
            role="menuitem"
            className={item}
            disabled={copying}
            onClick={async () => { setOpen(false); await onDuplicate() }}
            title={t('catalog.plans.duplicateHint', {
              defaultValue: 'Create a retired copy of this preset, including its caps, models and rhythm.',
            })}
          >
            {copying ? t('common:saving') : t('catalog.plans.duplicate', { defaultValue: 'Duplicate' })}
          </button>
        </div>,
        document.body,
      )}
    </div>
  )
}

/**
 * Which AI models each plan includes.
 *
 * A models × plans grid rather than a picker per plan, for the same reason
 * FeaturePricingAdmin uses a matrix: the question an operator actually asks is
 * "which plans include 5.5 Pro?", and a per-plan editor behind a dropdown can
 * only answer it by opening every plan in turn.
 *
 * A blank ROW (no plan ticked) is the state to watch: that model is in no plan,
 * so every tenant sees it as an upsell with no plan to name. Flagged inline —
 * it is the difference between "premium" and "unreachable".
 */
/**
 * Per-plan overage: whether usage beyond the included budget bills at all, and
 * at what rate.
 *
 * ── Why this exists ──────────────────────────────────────────────────
 *
 * `overage_enabled` defaults false and `overage_rate_myr_cents_per_usd`
 * defaults NULL, and until this section there was no UI for either. So every
 * plan created through this screen silently never billed overage:
 * `addOverageToInvoice` returned "plan X does not bill overage", the usage was
 * metered and stored, and the customer's billing page showed "Overage so far:
 * 0.00" while they consumed. Revenue quietly lost, with the screen agreeing
 * that nothing was owed.
 *
 * ── Its own section, not two more table columns ──────────────────────
 *
 * The plan table already carries ten columns and scrolls horizontally. These
 * two settings are also a PAIR — a rate without the toggle bills nothing, a
 * toggle without a rate is the silent state above — so they read better
 * together than as neighbouring columns that happen to be adjacent.
 *
 * ── The rate's unit ──────────────────────────────────────────────────
 *
 * Stored as MINOR UNITS of the plan's currency per US$1.00 of metered usage
 * (550 = RM5.50/USD). The input is in MAJOR units and goes through
 * `parsePrice`/`priceToInput` with the plan's own currency, so the exponent
 * comes from the currency table rather than an assumed 100 — the same
 * discipline as the price column, and the reason a JPY plan is not 100× off.
 */
// ── PlanOverage is GONE (Gate 1) ────────────────────────────────────────
//
// It edited `billing_plans.overage_enabled` / `.overage_rate_myr_cents_per_usd`
// / `.default_overage_cap_usd`, all three of which are dropped with the rest of
// the plan's USD money. Postpaid overage is retired: no plan sells it, the
// subscription source resolves `extraCapBillingMicro = 0` for every tenant, and
// the invoice line that billed it converted USD spend at a rate column that no
// longer exists — in the native model that spend is already denominated in the
// invoice's own currency.
//
// Reviving it needs a PRICED extra ceiling on `billing_plan_prices` (§10), and
// this editor would then belong beside the price row rather than on the plan.
/**
 * Per-principal request ceilings, per plan.
 *
 * A rate limit is a SAFETY VALVE, not a priced product — the signals doc sizes the
 * defaults "to stop a runaway loop, not to price compute". This screen only lets an
 * operator RAISE the ceiling for a plan whose customers legitimately outgrew the
 * default; the limiter takes `Math.max(envDefault, planValue)` so a mistyped low
 * number can never throttle a customer below the deployment's own floor.
 *
 * Blank = inherit the deployment default. It is NOT zero: a ceiling of 0 would 429
 * every call, so `normalisePlanRateLimits` drops non-positive values and the key
 * disappears from the blob entirely.
 */
export function PlanRateLimitsEditor({
  plans, busy, onSaved, bar, savers, inheritedBaseline, draftKey, t,
}: {
  plans: AdminPlanView[]
  busy: boolean
  onSaved: () => Promise<void>
  bar?: PlanEditBar
  /** Injected (P1 Task 3) — see `PlanUploads`' note on the single-plan bind. */
  savers: PlanSavers
  /**
   * The inheritance BASELINE per tier — the value a blank field falls back to.
   * Omitted (plan / plan-create scope): the platform default, because a blank
   * plan tier inherits the platform default. Provided (TENANT scope): the
   * PLAN's own rate limits, because a blank tenant tier inherits the plan's
   * value per tier (`resolveTenantRateLimit` falls through tier-by-tier). Without
   * this the placeholder shows the platform default while enforcement uses the
   * plan's — a shown≠enforced drift on a partial tenant override. `plans[i]`
   * here carries the tenant's OWN overrides (empty tiers stay blank and save as
   * inherited), so the two together keep display and save both correct.
   */
  inheritedBaseline?: Partial<Record<PlanRateLimitTier, number>>
  /** #897 — names this section's staged edits (scope + item) so they survive a
   *  phone/desktop layout swap. Absent = plain local state. */
  draftKey?: string
  t: T
}) {
  const toast = useToast()
  const [drafts, setDrafts, { clear: clearDrafts }] = useDraftState<Record<string, string>>(draftKey ?? null, NO_EDITS)
  const [saving, setSaving] = useState(false)

  const key = (planId: string, tier: PlanRateLimitTier) => `${planId}:${tier}`
  const stored = (p: AdminPlanView, tier: PlanRateLimitTier) => {
    const v = resolvePlanRateLimit(p.features as { rateLimits?: unknown } | null, tier)
    return v === null ? '' : String(v)
  }
  const valueFor = (p: AdminPlanView, tier: PlanRateLimitTier) => {
    const k = key(p.id, tier)
    return drafts[k] !== undefined ? drafts[k] : stored(p, tier)
  }

  const dirtyPlans = plans.filter((p) =>
    PLAN_RATE_LIMIT_TIERS.some((tier) => {
      const k = key(p.id, tier)
      return drafts[k] !== undefined && drafts[k] !== stored(p, tier)
    }),
  )

  const save = async (batch: PinnedBatch = pinnedBatch()) => {
    // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it
    // (`batch` — the bar's one batch, else this section's own); nothing is shown or reloaded once that session ended
    setSaving(true)
    try {
      for (const p of dirtyPlans) {
        // MERGE patch — touch only `rateLimits`. See catalog-admin.ts's note: a
        // whole-object replace here reverted the uploads toggle under one Save.
        const next: Record<string, number> = {}
        for (const tier of PLAN_RATE_LIMIT_TIERS) {
          const raw = valueFor(p, tier).trim()
          if (raw === '') continue
          const n = Number(raw)
          if (Number.isFinite(n) && n > 0) next[tier] = Math.floor(n)
        }
        // Routed through the injected saver (P1 Task 3) — for
        // `scope.kind === 'plan'` it IS this same `catalogAdminApi.updatePlan` call.
        await savers.saveRateLimits(normalisePlanRateLimits(next) ?? null, batch)
      }
      clearDrafts()
      if (batch.ended()) return
      if (!bar) {
        toast.success(t('catalog.plans.rateLimits.saved', {
          count: dirtyPlans.length,
          defaultValue: 'Request ceilings updated on {{count}} plan.',
          defaultValue_other: 'Request ceilings updated on {{count}} plans.',
        }))
      }
      await onSaved()
    } catch (e: any) {
      if (bar) throw e
      if (batch.ended()) return
      toast.error(e?.message || t('catalog.err.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const discard = () => clearDrafts()
  useRegisterSection(bar, 'rateLimits', dirtyPlans.length, save, discard)

  if (plans.length === 0) return null

  return (
    <div className="mt-4">
      <div className="text-xs themed-text-muted mb-2 flex items-center gap-1">
        {t('catalog.plans.rateLimits.title', { defaultValue: 'Request limits' })}
        <InfoHint text={inheritedBaseline
          ? t('catalog.plans.rateLimits.hintTenant', {
              defaultValue:
                'Requests per minute, per person. Blank inherits this tenant’s plan limit (shown) — set one only to override the plan for this tenant.',
            })
          : t('catalog.plans.rateLimits.hint', {
              defaultValue:
                'Requests per minute, per person — not per organisation, so colleagues never throttle each other. Blank inherits the platform default (shown). A plan can only raise the ceiling, never lower it below the default.',
            })} />
      </div>
      <div className="themed-bg-secondary rounded-lg overflow-hidden">
        {plans.map((p) => {
          const platformDefaults = resolvePlanFieldDefaults().rateLimits
          return (
          <div key={p.id} className="flex items-center gap-3 px-3 py-2 border-b themed-border last:border-b-0">
            <div className="flex-1 min-w-0 themed-text text-sm truncate">{p.name}</div>
            {PLAN_RATE_LIMIT_TIERS.map((tier) => {
              // F1/F2: the baseline is the placeholder; a saved value that
              // differs is an override, so it (and only it) offers a reset. The
              // baseline is the platform default in plan scope, or (tenant
              // scope) the PLAN's own value for this tier — so a blank tenant
              // tier shows what it actually inherits + enforces, never the
              // platform default it does NOT inherit through.
              const def = inheritedBaseline?.[tier] ?? platformDefaults[tier]
              const savedNum = resolvePlanRateLimit(p.features as { rateLimits?: unknown } | null, tier)
              const field = inheritedFieldState(savedNum, def)
              return (
              <label key={tier} className="flex items-center gap-1 text-xs themed-text-muted">
                <span className="uppercase tracking-wide">{tier}</span>
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  max={MAX_PLAN_RATE_LIMIT}
                  disabled={busy || saving}
                  className="themed-input w-24 text-sm"
                  placeholder={String(def)}
                  value={valueFor(p, tier)}
                  onChange={(e) =>
                    setDrafts((d) => withEdit(d, key(p.id, tier), e.target.value === stored(p, tier) ? undefined : e.target.value))
                  }
                />
                {field.overridden && (
                  <button
                    type="button"
                    disabled={busy || saving}
                    // Reset = return to inheriting the default (clear the override).
                    onClick={() => setDrafts((d) => withEdit(d, key(p.id, tier), stored(p, tier) === '' ? undefined : ''))}
                    title={t('catalog.plans.rateLimits.reset', {
                      defaultValue: 'Reset to default {{n}}', n: field.resetTo,
                    })}
                    className="text-[10px] themed-text-muted hover:themed-text underline underline-offset-2 disabled:opacity-50"
                  >
                    {t('catalog.plans.rateLimits.resetShort', { defaultValue: 'Reset {{n}}', n: field.resetTo })}
                  </button>
                )}
              </label>
              )
            })}
          </div>
          )
        })}
      </div>
    </div>
  )
}

export function PlanUploads({
  plans,
  busy,
  onSaved,
  bar,
  savers,
  draftKey,
  t,
}: {
  plans: AdminPlanView[]
  busy: boolean
  onSaved: () => Promise<void>
  bar?: PlanEditBar
  /** Injected (P1 Task 3) — see `plan-editor-scope.ts`. Only ever called with
   *  a single-element `plans` array (from `<PlanEditor>`), so one `savers`
   *  bound to that one plan covers every id the loop below can produce. */
  savers: PlanSavers
  /** #897 — names this section's staged edits (scope + item) so they survive a
   *  phone/desktop layout swap. Absent = plain local state. */
  draftKey?: string
  t: T
}) {
  const toast = useToast()
  const [drafts, setDrafts, { clear: clearDrafts }] = useDraftState<Record<string, boolean>>(draftKey ?? null, NO_EDITS)
  const [saving, setSaving] = useState(false)

  /** `true` = this plan PAUSES new items. Seeded from the server every reload. */
  const frozenFor = (p: AdminPlanView): boolean =>
    drafts[p.id] !== undefined ? drafts[p.id] : planBlocksCreate(p.features)

  const dirty = Object.keys(drafts).filter((id) => {
    const plan = plans.find((p) => p.id === id)
    return !!plan && drafts[id] !== planBlocksCreate(plan.features)
  })

  const save = async (batch: PinnedBatch = pinnedBatch()) => {
    // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it
    // (`batch` — the bar's one batch, else this section's own); nothing is shown or reloaded once that session ended
    setSaving(true)
    try {
      for (const id of dirty) {
        // MERGE patch — touch only `canCreateContent`. `null` deletes, which is how a
        // plan is UN-paused (absence is the unrestricted state; every plan row predates
        // this key). A whole-object replace here is what the sibling sections could
        // revert, and vice versa. Routed through the injected saver (P1 Task 3) — for
        // `scope.kind === 'plan'` it IS this same `catalogAdminApi.updatePlan` call.
        await savers.saveUploads(drafts[id] ? false : null, batch)
      }
      clearDrafts()
      if (batch.ended()) return
      if (!bar) {
        toast.success(t('catalog.plans.uploads.saved', {
          count: dirty.length,
          defaultValue: 'Upload permission updated on {{count}} plan.',
          defaultValue_other: 'Upload permission updated on {{count}} plans.',
        }))
      }
      await onSaved()
    } catch (e: any) {
      if (bar) throw e
      if (batch.ended()) return
      toast.error(e?.message || t('catalog.err.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const discard = () => clearDrafts()
  useRegisterSection(bar, 'uploads', dirty.length, save, discard)

  if (plans.length === 0) return null

  return (
    <div className="mt-4">
      {/* ONE line per plan: label + info + toggle + status chip. The title is
          not repeated separately — checked = uploads ALLOWED; the stored flag is
          the inverse (`canCreateContent: false` pauses), so `frozen = !checked`. */}
      <div className="themed-bg-secondary rounded-lg overflow-hidden">
        {plans.map((p) => {
          const frozen = frozenFor(p)
          return (
            <div
              key={p.id}
              className="flex items-center gap-2 px-3 py-2 border-b themed-border last:border-b-0"
            >
              <span className="themed-text text-sm">
                {t('catalog.plans.uploads.toggle', { defaultValue: 'Allow uploads' })}
              </span>
              <InfoHint
                text={t('catalog.plans.uploads.desc', {
                  defaultValue:
                    'When off, this plan blocks NEW files and folders for every tenant on it. They keep full read access and can still edit what they already have — it is not a suspension. This is the lever manual billing\'s fallback plan pulls.',
                })}
              />
              <ToggleSwitch
                checked={!frozen}
                disabled={busy || saving}
                onChange={(v) => setDrafts((cur) => withEdit(cur, p.id, !v === planBlocksCreate(p.features) ? undefined : !v))}
                label={`${p.name} ${t('catalog.plans.uploads.toggle', { defaultValue: 'Allow uploads' })}`}
              />
              <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${
                frozen ? 'bg-amber-500/20 text-amber-300' : 'themed-text-muted'
              }`}>
                {frozen
                  ? t('catalog.plans.uploads.paused', { defaultValue: 'Uploads paused' })
                  : t('catalog.plans.uploads.allowed', { defaultValue: 'Uploads allowed' })}
              </span>
            </div>
          )
        })}
      </div>
      {/* Own bar only when NOT driven by the dialog's single Save bar. */}
      {!bar && dirty.length > 0 && (
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => void save()}
            disabled={busy || saving}
            className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
          >
            {saving ? t('common:saving') : t('common:save')}
          </button>
          <button
            onClick={discard}
            disabled={saving}
            className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
          >
            {t('catalog.discard', { defaultValue: 'Discard' })}
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Per-plan metered feature pricing (web search, sandbox compute), folded into
 * the plan editor from the old global "metered defaults" section.
 *
 * Pricing resolves tenant → plan → global → free. This edits the PLAN rung: a
 * per-plan rate/chargeable, or blank to inherit the deployment default. Rates
 * are USD microdollars on the wire, dollars on screen (like `LlmPricing`) — the
 * cost is USD because the providers invoice us in USD.
 *
 * Kept compact deliberately: it edits `chargeable` + the base rate, and "Reset"
 * DELETEs the override (the faithful "inherit" — a PUT of nulls leaves a dead
 * row that still wins `mode`/`tiers`, which the server route documents). Tiered
 * pricing is preserved untouched because only these two fields are ever sent.
 */
const microToDollars = (micro: number | null | undefined) => (micro == null ? '' : String(micro / 1_000_000))

/**
 * Metered feature pricing at ONE scope — a PLAN or a TENANT — so the price book's
 * plan editor and the per-tenant Configuration screen present it identically
 * (the same `scope` precedent as `LlmPricing`). The only differences are the
 * three endpoints and the inherit base: a plan inherits the deployment default,
 * a tenant inherits the plan (then the deployment default).
 */
/** The draft `FeaturePricingEditor` reads/writes in plan-CREATE mode — one
 *  entry per priceable feature (`chargeable` + a major-unit `rate` string,
 *  blank = inherit). Held on `PlanCreateDraft.meteredDraft` and persisted by
 *  `confirmDraft`. */
export type MeteredDraft = Record<string, { chargeable: boolean; rate: string }>

export function FeaturePricingEditor({ scope, scopeId, busy, bar, t, currency = 'usd', draftValue, onDraftChange, draftKey }: {
  scope: 'plan' | 'tenant'
  scopeId: string
  busy: boolean
  /** Plan dialog only: register into the single Save bar and hide this one. */
  bar?: PlanEditBar
  t: T
  /**
   * Lowercase ISO-4217 — which currency's rate row this editor reads and
   * writes (0187's currency axis on per-plan/per-tenant feature pricing).
   * Default `'usd'` keeps this byte-identical to pre-P1 behaviour. The
   * currency SELECTOR is P1 Task 5 — this prop only threads the value
   * through.
   */
  currency?: string
  /**
   * DRAFT (plan-CREATE) MODE (0190 Phase 6, Task 9). When BOTH `draftValue`
   * and `onDraftChange` are supplied, the editor stops being fetch-driven:
   * it seeds a blank row SKELETON from the static catalog
   * (`PER_TENANT_PRICEABLE`) instead of `api.get()`, renders values from
   * `draftValue`, and mirrors every edit up through `onDraftChange` instead
   * of network-saving. Persistence happens in the host's `confirmDraft`, so
   * the registered `save` is a no-op. Absent ⇒ unchanged fetch-driven (EDIT)
   * behaviour. */
  draftValue?: MeteredDraft
  onDraftChange?: (next: MeteredDraft) => void
  /** #897 — names the EDIT-mode drafts (scope + item; the currency is added
   *  here) so they survive a phone/desktop layout swap. Draft (create) mode
   *  needs none: its edits already live in the host's draft. */
  draftKey?: string
}) {
  const toast = useToast()
  const draftMode = onDraftChange !== undefined
  const [rows, setRows] = useState<PlanFeaturePricingRow[] | null>(null)
  const [base, setBase] = useState<Record<string, { chargeable: boolean; rate: string }>>(NO_EDITS)
  // The TOUCHED features only, laid over `base` — a load never overwrites an
  // edit (a swap's first load must not wipe the draft it carried), and a
  // feature the operator never touched always shows (and saves) the freshly
  // loaded value: a whole-map copy taken at the first edit carried every other
  // feature's old value across a swap, and Save reverted what someone else had
  // changed since.
  const storeKey = !draftMode && draftKey ? `${draftKey}:${currency}` : null
  const [edits, setEdits, { clear: clearDrafts }] = useDraftState<Record<string, { chargeable: boolean; rate: string }>>(storeKey, NO_EDITS)
  const drafts = useMemo(() => ({ ...base, ...edits }), [base, edits])
  const [saving, setSaving] = useState(false)

  // The three scoped endpoints, chosen once. Unused (never called) in draft
  // mode — the closures below reference `catalogAdminApi`, they do not invoke
  // it until `load`/`save`, both of which short-circuit when `draftMode`.
  const api = scope === 'plan'
    ? {
        get: () => catalogAdminApi.getPlanFeaturePricing(scopeId, currency),
        set: (f: string, v: { chargeable?: boolean | null; baseRateUsd?: number | null }) => catalogAdminApi.setPlanFeaturePricing(scopeId, f, { ...v, currency }),
        clear: (f: string) => catalogAdminApi.clearPlanFeaturePricing(scopeId, f, currency),
      }
    : {
        get: () => catalogAdminApi.getTenantFeaturePricing(scopeId, currency),
        set: (f: string, v: { chargeable?: boolean | null; baseRateUsd?: number | null }) => catalogAdminApi.setTenantFeaturePricing(scopeId, f, { ...v, currency }),
        clear: (f: string) => catalogAdminApi.clearTenantFeaturePricing(scopeId, f, currency),
      }
  const inheritsFrom = scope === 'plan'
    ? t('catalog.metered.inheritsDefault', { defaultValue: 'the deployment default' })
    : t('catalog.metered.inheritsPlan', { defaultValue: 'the plan' })

  // The value this scope INHERITS when it has no override of its own (a tenant
  // inherits the plan then the global; a plan inherits the global directly).
  const inheritCharge = (r: PlanFeaturePricingRow) => r.planLayer?.chargeable ?? r.global?.chargeable ?? false
  const inheritRate = (r: PlanFeaturePricingRow): number | null => r.planLayer?.baseRateUsd ?? r.global?.unitRateUsd ?? null
  // A tiered override cannot be represented by this flat editor — leave it alone.
  const isTiered = (r: PlanFeaturePricingRow) => r.override?.mode === 'tiered'

  const load = useCallback(async () => {
    // ── DRAFT MODE: seed a blank skeleton from the static catalog, no fetch.
    // A new plan inherits the deployment default for every feature (chargeable
    // off, rate blank ⇒ "inherit"); those are the row's synthetic `global`/
    // `override` (both null). The operator's edits live in `draftValue`.
    if (draftMode) {
      const skeleton: PlanFeaturePricingRow[] = PER_TENANT_PRICEABLE.map(feature => ({
        feature, global: null, override: null, source: 'global',
      }))
      setRows(skeleton)
      const empty = Object.fromEntries(PER_TENANT_PRICEABLE.map(f => [f, { chargeable: false, rate: '' }]))
      setEdits(Object.fromEntries(PER_TENANT_PRICEABLE.map(f => [f, draftValue?.[f] ?? { chargeable: false, rate: '' }])))
      // `base` is the pristine (all-inherit) state, so the dirty count reflects
      // any value the operator has set — never the network baseline.
      setBase(empty)
      return
    }
    try {
      const res = await api.get()
      setRows(res.pricing)
      const d = Object.fromEntries(res.pricing.map(r => {
        // `chargeable` reflects the CURRENT effective state (for the checkbox);
        // `rate` is seeded from THIS scope's OWN override ONLY — blank means
        // "inherit the rate", and the inherited value is shown as a PLACEHOLDER.
        // Seeding the box with the inherited number would pin it as a hard
        // override on the next save, silently breaking future propagation.
        const inh = r.planLayer?.chargeable ?? r.global?.chargeable ?? false
        const chargeable = r.override?.chargeable ?? inh
        const rate = r.override?.baseRateUsd != null ? microToDollars(r.override.baseRateUsd) : ''
        return [r.feature, { chargeable, rate }]
      }))
      setBase(d)
      // Unnamed (local) drafts re-seed on every load, as they always did; a
      // named draft is dropped only by Save / reset / Discard.
      if (!storeKey) clearDrafts()
    } catch {
      setRows([])
    }
  }, [scope, scopeId, currency, storeKey, clearDrafts])

  useEffect(() => { void load() }, [load])

  const featureLabel = (f: string) =>
    t(`catalog.metered.feature.${f}`, { defaultValue: f.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) })

  const dirty = rows?.some(r => {
    const a = drafts[r.feature], b = base[r.feature]
    return a && b && (a.chargeable !== b.chargeable || a.rate.trim() !== b.rate.trim())
  }) ?? false

  const save = async (batch: PinnedBatch = pinnedBatch()) => {
    // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it
    // (`batch` — the bar's one batch, else this section's own); nothing is shown or reloaded once that session ended
    // Draft mode persists nothing here — the host's `confirmDraft` writes the
    // metered rows after the plan row exists. Every edit already reached the
    // draft via `patch` → `onDraftChange`, so there is nothing to flush.
    if (draftMode) return
    if (!rows) return
    // Validate: a non-blank rate must parse to a non-negative number. Shared
    // with `confirmDraft` (create) via `isValidRateField` so the two can't drift.
    for (const r of rows) {
      const dr = drafts[r.feature]
      if (!isValidRateField(dr.rate)) {
        toast.error(t('catalog.metered.err.rate', { defaultValue: 'Rate for {{f}} is not a valid amount', f: featureLabel(r.feature) }))
        return
      }
    }
    setSaving(true)
    try {
      for (const r of rows) {
        const dr = drafts[r.feature], bs = base[r.feature]
        if (dr.chargeable === bs.chargeable && dr.rate.trim() === bs.rate.trim()) continue
        if (isTiered(r)) continue  // never overwrite a tiered override from here
        // PER-AXIS inheritance: send `null` for an axis that MATCHES the inherited
        // value (so it keeps inheriting), and the explicit value otherwise. A
        // rate-only edit therefore does NOT pin `chargeable`, and unchecking
        // `chargeable` does NOT pin the rate. If both axes end up inheriting, the
        // override is DELETED so no dead row wins mode/tiers.
        const rateNull = dr.rate.trim() === ''
        const chargeNull = dr.chargeable === inheritCharge(r)
        if (rateNull && chargeNull) {
          await batch.run(() => api.clear(r.feature))
        } else {
          await batch.run(() => api.set(r.feature, {
            chargeable: chargeNull ? null : dr.chargeable,
            baseRateUsd: rateNull ? null : Math.round(Number(dr.rate) * 1_000_000),
          }))
        }
      }
      if (batch.ended()) { clearDrafts(); return }
      if (!bar) toast.success(t('catalog.metered.saved', { defaultValue: 'Metered pricing saved.' }))
      await load()
      clearDrafts()
    } catch (e: any) {
      if (bar) throw e
      if (batch.ended()) return
      toast.error(e?.message || t('catalog.metered.err.save', { defaultValue: 'Could not save metered pricing.' }))
    } finally {
      setSaving(false)
    }
  }

  useRegisterSection(bar, `metered:${scope}:${scopeId}`, dirty ? 1 : 0, save, () => {
    // Draft mode's local draft returns to `base` the same way.
    clearDrafts()
    if (draftMode) onDraftChange?.(base)
  })

  const reset = async (feature: string) => {
    setSaving(true)
    try {
      await api.clear(feature)
      toast.success(t('catalog.metered.resetDoneScoped', { defaultValue: 'Reverted to {{base}}.', base: inheritsFrom }))
      await load()
      clearDrafts()
    } catch (e: any) {
      toast.error(e?.message || t('catalog.metered.err.save', { defaultValue: 'Could not save metered pricing.' }))
    } finally {
      setSaving(false)
    }
  }

  if (!rows) return null
  const patch = (feature: string, p: Partial<{ chargeable: boolean; rate: string }>) => {
    const row = { ...drafts[feature], ...p }
    setEdits(prev => withEdit(prev, feature, sameJson(row, base[feature]) ? undefined : row))
    // Draft mode mirrors the whole map up on every edit — the draft, not this
    // component's own state, is the source of truth `confirmDraft` reads.
    if (draftMode) onDraftChange?.({ ...drafts, [feature]: row })
  }

  return (
    <Section
      title={t('catalog.metered.title', { defaultValue: 'Metered feature pricing' })}
      info={t('catalog.metered.descScoped', {
        defaultValue: 'What web search and sandbox compute cost. Blank inherits {{base}} (free unless set).',
        base: inheritsFrom,
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3 flex flex-col gap-2">
        {/* Iterate the SERVER's priceable features, not a hardcoded copy — a new
            metered feature added server-side then shows here automatically. */}
        {rows.map(r => {
          const feature = r.feature
          const dr = drafts[feature]
          if (!dr) return null
          const unit = r.global?.unit ?? ''
          const hasOverride = r.source === 'override'
          const tiered = isTiered(r)
          // The rate this scope falls back to when its own box is blank — shown
          // as the input's placeholder so the operator sees what "inherit" means
          // without it being seeded as a hard value.
          const inhRateMicro = inheritRate(r)
          const inhRatePlaceholder = inhRateMicro != null
            ? microToDollars(inhRateMicro)
            : t('catalog.metered.inherit', { defaultValue: 'inherit' })
          return (
            <div key={feature} className="flex items-center gap-3 flex-wrap">
              <span className="themed-text text-sm w-40">{featureLabel(feature)}</span>
              <label className="flex items-center gap-1.5 text-xs themed-text">
                <input
                  type="checkbox"
                  checked={dr.chargeable}
                  disabled={busy || saving || tiered}
                  onChange={e => patch(feature, { chargeable: e.target.checked })}
                />
                {t('catalog.metered.chargeable', { defaultValue: 'Chargeable' })}
              </label>
              {tiered ? (
                <span className="themed-text-muted text-xs italic">
                  {t('catalog.metered.tiered', { defaultValue: 'Tiered rate — edit in advanced pricing' })}
                </span>
              ) : (
              <div className="flex items-center gap-1">
                <span className="themed-text-muted text-[10px]">{currency.toUpperCase()}</span>
                <input
                  value={dr.rate}
                  disabled={busy || saving}
                  onChange={e => patch(feature, { rate: e.target.value })}
                  inputMode="decimal"
                  placeholder={inhRatePlaceholder}
                  aria-label={`${featureLabel(feature)} ${t('catalog.metered.rate', { defaultValue: 'rate' })}`}
                  className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
                />
                {unit && <span className="themed-text-muted text-[10px]">/ {unit}</span>}
              </div>
              )}
              {/* A real DELETE of the override — the only way back to full
                  inheritance of the deployment default. Shown only when a plan
                  override actually exists. */}
              {hasOverride && (
                <button
                  onClick={() => { void reset(feature) }}
                  disabled={busy || saving}
                  className="text-[11px] themed-text-muted hover:themed-text underline disabled:opacity-50"
                >
                  {t('catalog.metered.reset', { defaultValue: 'Reset to default' })}
                </button>
              )}
            </div>
          )
        })}
        {!bar && dirty && (
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => { void save() }}
              disabled={busy || saving}
              className="px-2.5 py-1 text-xs rounded themed-btn-primary disabled:opacity-50"
            >
              {saving ? t('catalog.metered.saving', { defaultValue: 'Saving…' }) : t('catalog.metered.save', { defaultValue: 'Save metered pricing' })}
            </button>
            <button
              onClick={() => clearDrafts()}
              disabled={busy || saving}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
            >
              {t('catalog.metered.discard', { defaultValue: 'Discard' })}
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

/**
 * LLM prices — the largest spend surface, and until 0167 the only one an
 * operator could not change without a deploy.
 *
 * ── What this edits, and what it does not ────────────────────────────────
 *
 * The RESALE price. `providers.ts` stays the authority on what the provider
 * charges us, and both numbers are recorded, so a discount here reads as a
 * discount on the margin report rather than as the provider getting cheaper.
 *
 * ── The GLOBAL layer only ────────────────────────────────────────────────
 *
 * The chain is tenant → plan → global → shipped, and this screen edits the
 * bottom configurable rung. The plan and tenant rungs are edited where the plan
 * and the tenant live — the same filing rule the rest of the console follows,
 * and the reason per-tenant feature pricing sits on the customer page rather
 * than here. A three-scope switcher on the price book would put "reprice every
 * customer" and "reprice this one" one mis-click apart.
 *
 * ── Units ────────────────────────────────────────────────────────────────
 *
 * AI cost is always METERED in USD — the providers invoice us in USD, and
 * that side is untouched by any of this. What this grid edits is the RESALE
 * price an operator quotes a tenant, and since 0186/0187 that price may be
 * quoted per CURRENCY (the `currency` prop, threaded into
 * `listModelPricing`/`setModelPricing`) rather than only in USD — three
 * cases, matching `PriceSetPanel`'s labelling (P1 Task 5): a price stored
 * under the deployment's own billing currency is `charge_basis='price'` and
 * charged DIRECTLY at that number; a USD price is the FALLBACK — consulted
 * when there is no billing-currency price, charged via the credit rate
 * (`charge_basis='rate'`, design §3.1/§3.5); a price stored under any OTHER
 * currency (neither billing nor USD) is reserved/not-charged on a
 * single-deployment-currency system. So a currency symbol on screen here is
 * no longer a whitelabel bug — it is the currency this particular price-set
 * is quoted in, and the column header renders it via
 * `currency.toUpperCase()` rather than a hardcoded 'USD'.
 */
/** The draft `LlmPricing` reads/writes in plan-CREATE mode — the per-model
 *  price overrides (major-unit strings, blank = inherit built-in) AND the
 *  plan's model allow-list (`null` = unrestricted). Held on
 *  `PlanCreateDraft.modelDraft` and persisted by `confirmDraft`. */
export interface LlmPricingDraft {
  prices: Record<string, { input: string; output: string }>
  allowed: string[] | null
}

export function LlmPricing({ busy, t, scope = 'global', scopeId, title, desc, allowedModels, onAllowedSave, bar, currency = 'usd', draftValue, onDraftChange, draftKey }: {
  busy: boolean
  t: T
  /**
   * Which rung of the tenant → plan → global ladder this instance edits (5a).
   *
   * The API has supported all three since 0167; the UI only ever wrote
   * `global`, so a discount for one customer — on the surface that is 96% of
   * spend — was reachable only by calling the endpoint directly. Compute and
   * web search have had the full ladder all along.
   */
  scope?: 'global' | 'plan' | 'tenant'
  /** Required for `plan` and `tenant`; the server 400s without it. */
  scopeId?: string
  title?: string
  desc?: string
  /**
   * The scope's model ALLOW-LIST, folded into this same grid so an operator sets
   * "may they use it" and "what does it cost" in one place rather than two
   * sections. `null` = unrestricted (every listed model allowed). Provide
   * `onAllowedSave` too, or the Allowed column does not render (global has no
   * allow-list — the plan/tenant rungs do). The write path is INJECTED because a
   * plan writes `billing_plans.features.models` (@papan/billing) and a tenant
   * writes `tenant_settings.allowed_models` (@papan/orgs), and neither package
   * may import the other.
   */
  allowedModels?: string[] | null
  onAllowedSave?: (next: string[] | null) => Promise<void>
  /** Plan dialog only: register into the single Save bar and hide this one. */
  bar?: PlanEditBar
  /**
   * Lowercase ISO-4217 — which currency's price row this grid reads and
   * writes (P0 Task 14 / 0187's currency axis on `model_pricing`). Default
   * `'usd'` keeps this byte-identical to pre-P1 behaviour, since every
   * existing row is `'usd'`. The price-set currency SELECTOR that lets an
   * operator change this is P1 Task 5 — this prop only threads the value
   * through.
   */
  currency?: string
  /**
   * DRAFT (plan-CREATE) MODE (0190 Phase 6, Task 9). See `LlmPricingDraft`.
   */
  draftValue?: LlmPricingDraft
  onDraftChange?: (next: LlmPricingDraft) => void
  /** #897 — names the EDIT-mode drafts (scope + item) so they survive a
   *  phone/desktop layout swap. Draft (create) mode needs none: its edits
   *  already live in the host's draft. */
  draftKey?: string
}) {
  const toast = useToast()
  const draftMode = onDraftChange !== undefined
  const [rows, setRows] = useState<Awaited<ReturnType<typeof catalogAdminApi.listModelPricing>>['models'] | null>(null)
  // Prices are per currency; the allow-list is not.
  const priceKey = !draftMode && draftKey ? `${draftKey}:prices:${currency}` : null
  const allowStoreKey = !draftMode && draftKey ? `${draftKey}:allowed` : null
  const [drafts, setDrafts, { clear: clearDrafts }] = useDraftState<Record<string, { input: string; output: string }>>(priceKey, NO_EDITS)
  const [saving, setSaving] = useState(false)

  // Draft mode folds the allow-list into this grid from the draft (no injected
  // `onAllowedSave` — a new plan writes `features.models` in `confirmDraft`).
  const allowEnabled = onAllowedSave !== undefined || draftMode
  // `null` = unrestricted (all listed models). Seeded from the prop and re-seeded
  // whenever the parent reloads it (its `onAllowedSave` refreshes the source).
  // The dep is a stable key, not the array reference, so a parent that maps a
  // fresh array each render does not stomp an in-progress edit. In draft mode
  // the source is `draftValue.allowed` (seeded in `load`, then this component is
  // authoritative), so the prop-driven effect is inert (`allowedModels` unset).
  //
  // #897 — in EDIT mode with a `draftKey` the allow-list is a draft over the
  // prop (kept across a phone/desktop layout swap): until ticked it IS the
  // prop, so a reload shows through, and a reload never overwrites a tick.
  // Save / Discard drop it. Unnamed, the prop-keyed re-seed below still runs.
  const [initialAllow] = useState<string[] | null>(draftMode ? (draftValue?.allowed ?? null) : (allowedModels ?? null))
  const [allowDraft, setAllowDraft, { clear: clearAllowDraft }] = useDraftState<string[] | null>(
    allowStoreKey,
    allowStoreKey ? (allowedModels ?? null) : initialAllow,
    // "Unchanged" = the same models in any order (#897) — but an EMPTY list is
    // not "unrestricted" here, though Save treats it so: it is the moment the
    // empty-means-all warning exists for, and dropping it re-ticked every box.
    { isSame: (a, b) => (a === null || b === null ? a === b : a.length === b.length && allowNorm(a) === allowNorm(b)) },
  )
  const allowKey = allowedModels == null ? 'ALL' : [...allowedModels].sort().join('|')
  useEffect(() => { if (!draftMode && !allowStoreKey) setAllowDraft(allowedModels ?? null) }, [allowKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    // ── DRAFT MODE: seed rows from the static catalog, no fetch. A new plan
    // overrides nothing (blank ⇒ inherits the built-in rate), so `override` is
    // null and `shipped` is the catalog's built-in price. Mock models are not
    // sellable, so they are excluded — same set `listModelPricing` returns.
    if (draftMode) {
      const skeleton = MODEL_CATALOG.filter(m => m.provider !== 'mock').map(m => ({
        modelId: m.id,
        label: m.label,
        shipped: { input: Math.round(m.inputCost * 1_000_000), output: Math.round(m.outputCost * 1_000_000) },
        override: null,
      }))
      setRows(skeleton)
      setDrafts(draftValue?.prices ?? {})
      return
    }
    try {
      const res = await catalogAdminApi.listModelPricing(scope, scopeId, currency)
      setRows(res.models)
      // Unnamed (local) drafts reset on every load, as they always did; a named
      // draft is dropped only by Save / Discard.
      if (!priceKey) setDrafts({})
    } catch {
      setRows([])
    }
  }, [scope, scopeId, currency, priceKey, setDrafts])
  useEffect(() => { void load() }, [load])

  // NOTE: everything below is computed with `rows` possibly null and guarded,
  // because `useRegisterSection` (a hook) must run BEFORE the empty-rows early
  // return further down. `draftFor`/the grid only execute after that return.

  /** Microdollars per 1M → the dollars-per-1M string an operator types. */
  const toInput = (micro: number | null | undefined) =>
    micro === null || micro === undefined ? '' : String(micro / 1_000_000)

  // The allow-list operates over exactly the models this grid shows, so "all
  // allowed" means every checkbox ticked and clearing them all normalizes to
  // `null`. Toggling from unrestricted starts from the full row set minus the
  // one clicked — starting from `[id]` would silently strip every other model.
  const allowedIds = rows ? rows.map((r) => r.modelId) : []
  const isAllowed = (modelId: string) => allowDraft === null || allowDraft.includes(modelId)
  const toggleAllowed = (modelId: string) => {
    const cur = allowDraft ?? allowedIds
    const next = cur.includes(modelId) ? cur.filter((x) => x !== modelId) : [...cur, modelId]
    setAllowDraft(next)
    // Draft mode mirrors the whole {prices, allowed} shape up on every edit.
    if (draftMode) onDraftChange?.({ prices: drafts, allowed: next })
  }
  // Draft-mode price edit — mirror the merged price map up alongside the
  // current allow-list. Computed outside the state updater so StrictMode's
  // double-invoke can never fire `onDraftChange` twice.
  const editPrice = (r: NonNullable<typeof rows>[number], field: 'input' | 'output', value: string) => {
    const row = { ...draftFor(r), [field]: value }
    const loaded = { input: toInput(r.override?.inputUsdMicroPer1m), output: toInput(r.override?.outputUsdMicroPer1m) }
    setDrafts(withEdit(drafts, r.modelId, sameJson(row, loaded) ? undefined : row))
    if (draftMode) onDraftChange?.({ prices: { ...drafts, [r.modelId]: row }, allowed: allowDraft })
  }
  const allowNorm = (v: string[] | null) => (v && v.length > 0 ? [...v].sort().join('|') : 'ALL')
  const allowDirty = allowEnabled && allowNorm(allowDraft) !== allowNorm(allowedModels ?? null)

  const draftFor = (r: NonNullable<typeof rows>[number]) =>
    drafts[r.modelId] ?? {
      input: toInput(r.override?.inputUsdMicroPer1m),
      output: toInput(r.override?.outputUsdMicroPer1m),
    }

  const dirty = (rows ?? []).filter((r) => {
    const d = drafts[r.modelId]
    if (!d) return false
    const base = {
      input: toInput(r.override?.inputUsdMicroPer1m),
      output: toInput(r.override?.outputUsdMicroPer1m),
    }
    return d.input !== base.input || d.output !== base.output
  })

  const save = async (batch: PinnedBatch = pinnedBatch()) => {
    // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it
    // (`batch` — the bar's one batch, else this section's own); nothing is shown or reloaded once that session ended
    // Draft mode persists nothing here — `confirmDraft` writes the model
    // prices + allow-list after the plan row exists. Edits already reached
    // the draft via `editPrice`/`toggleAllowed` → `onDraftChange`.
    if (draftMode) return
    if (!rows) return
    setSaving(true)
    try {
      for (const r of dirty) {
        const d = draftFor(r)
        // Status rule shared with `confirmDraft` (create) via
        // `modelPriceFieldStatus`, so the two can't drift.
        const status = modelPriceFieldStatus(d.input, d.output)
        if (status === 'blank') {
          // BOTH blank clears the override.
          await batch.run(() => catalogAdminApi.setModelPricing({
            scope, scopeId, modelId: r.modelId, currency,
            inputUsdMicroPer1m: null, outputUsdMicroPer1m: null,
          }))
          continue
        }
        if (status === 'invalid') {
          toast.error(t('catalog.plans.llm.bad', {
            defaultValue: '{{model}}: both prices must be zero or more, or both blank to remove the override.',
            model: r.label,
          }))
          setSaving(false)
          return
        }
        await batch.run(() => catalogAdminApi.setModelPricing({
          scope, scopeId, modelId: r.modelId, currency,
          inputUsdMicroPer1m: Math.round(Number(d.input) * 1_000_000),
          outputUsdMicroPer1m: Math.round(Number(d.output) * 1_000_000),
        }))
      }
      // The allow-list write, if the operator touched a checkbox. An empty
      // selection is "unrestricted", not "block everything" (a budget cap of 0
      // does that), so it normalizes to `null`. The parent's `onAllowedSave`
      // refreshes the source, which re-seeds `allowDraft` via `allowKey`.
      if (allowDirty && onAllowedSave) {
        await batch.run(() => onAllowedSave(allowDraft && allowDraft.length > 0 ? allowDraft : null))
      }
      if (batch.ended()) { clearDrafts(); if (allowStoreKey) clearAllowDraft(); return }
      if (!bar && dirty.length > 0) {
        toast.success(t('catalog.plans.llm.saved', {
          count: dirty.length,
          defaultValue: 'Repriced {{count}} model.',
          defaultValue_other: 'Repriced {{count}} models.',
        }))
      }
      await load()
      clearDrafts()
      if (allowStoreKey) clearAllowDraft()
    } catch (e: any) {
      if (bar) throw e
      if (batch.ended()) return
      toast.error(e?.message || t('catalog.err.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const discard = () => {
    clearDrafts()
    const nextAllowed = draftMode ? null : (allowedModels ?? null)
    if (allowStoreKey) clearAllowDraft()
    else setAllowDraft(nextAllowed)
    if (draftMode) onDraftChange?.({ prices: {}, allowed: nextAllowed })
  }
  useRegisterSection(bar, `llm:${scope}:${scopeId ?? 'global'}`, dirty.length + (allowDirty ? 1 : 0), save, discard)

  // Empty-rows guard AFTER the hook above (rules of hooks).
  if (!rows || rows.length === 0) return null

  return (
    // A top-level `Section` (h3), so it lands in the "on this page" rail. As an
    // `h4` nested inside Plans it was one of the four headings the scroll-spy
    // could not see, which is why that rail claimed a 2,877px page had five
    // sections when it had nine.
    <Section
      title={title ?? t('catalog.plans.llm.title', { defaultValue: 'AI model prices' })}
      info={desc ?? t('catalog.plans.llm.desc', {
        defaultValue:
          'What tenants are charged per 1M tokens, in US dollars — the AI ledger is denominated in USD whatever this deployment bills in. Blank means the built-in price applies. Setting a price here does NOT change what the provider charges us: both are recorded, so the margin figure stays honest.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b themed-border">
              {(allowEnabled ? ['allowed', 'model', 'shipped', 'inputPrice', 'outputPrice'] : ['model', 'shipped', 'inputPrice', 'outputPrice']).map((col) => (
                <th key={col} className="text-left px-2 py-2 themed-text-secondary text-xs font-medium whitespace-nowrap">
                  {col === 'allowed' ? (
                    <span className="inline-flex items-center gap-1">
                      {t('catalog.plans.llm.col.allowed', { defaultValue: 'Allowed' })}
                      <InfoHint text={t('catalog.plans.llm.allowedHint', {
                        defaultValue: 'Unticked models are hidden from this scope’s picker — not shown as restricted. Every model ticked stores no restriction (all allowed). A model can be allowed and still priced below.',
                      })} />
                    </span>
                  ) : t(`catalog.plans.llm.col.${col}`, {
                    defaultValue: {
                      model: 'Model', shipped: 'Built-in price (in / out)',
                      inputPrice: `Input ${currency.toUpperCase()}/1M`, outputPrice: `Output ${currency.toUpperCase()}/1M`,
                    }[col],
                    currency: currency.toUpperCase(),
                  })}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const d = draftFor(r)
              const overridden = !!r.override
              return (
                <tr key={r.modelId} className="border-b themed-border last:border-b-0">
                  {allowEnabled && (
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={isAllowed(r.modelId)}
                        disabled={busy || saving}
                        onChange={() => toggleAllowed(r.modelId)}
                        aria-label={`${r.label} — ${t('catalog.plans.llm.col.allowed', { defaultValue: 'Allowed' })}`}
                      />
                    </td>
                  )}
                  <td className="px-2 py-2">
                    <span className="themed-text text-xs">{r.label}</span>
                    <span className="themed-text-muted text-[10px] font-mono ml-2">{r.modelId}</span>
                    {overridden && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
                        {t('catalog.plans.llm.overridden', { defaultValue: 'Repriced' })}
                      </span>
                    )}
                  </td>
                  {/* What clearing restores. Without it an operator cannot tell
                      whether an override is a discount or a markup. */}
                  <td className="px-2 py-2 themed-text-muted text-[11px] whitespace-nowrap">
                    {r.shipped
                      ? `$${(r.shipped.input / 1e6).toFixed(2)} / $${(r.shipped.output / 1e6).toFixed(2)}`
                      : '—'}
                  </td>
                  {(['input', 'output'] as const).map((field) => (
                    <td key={field} className="px-2 py-2">
                      <input
                        value={d[field]}
                        disabled={busy || saving}
                        inputMode="decimal"
                        placeholder={t('catalog.plans.llm.shippedPlaceholder', { defaultValue: 'built-in' })}
                        aria-label={`${r.label} — ${field}`}
                        onChange={(e) => editPrice(r, field, e.target.value)}
                        className="themed-input border rounded px-1.5 py-1 text-xs w-20 outline-none focus:border-blue-500 disabled:opacity-50"
                      />
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {/* Every model unticked reads as "block everything" and is NOT — the save
          normalizes it to "all allowed". Said before saving, like the tenant
          screen's old warning. */}
      {allowEnabled && Array.isArray(allowDraft) && allowDraft.length === 0 && (
        <p className="text-[11px] text-amber-300 mt-2">
          {t('catalog.plans.llm.emptyMeansAll', {
            defaultValue: 'No models ticked means unrestricted (all allowed), not "block everything". To stop all AI use, set a budget cap of 0.',
          })}
        </p>
      )}
      {!bar && (dirty.length > 0 || allowDirty) && (
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => void save()}
            disabled={busy || saving}
            className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
          >
            {saving ? t('common:saving') : t('common:save')}
          </button>
          <button
            onClick={discard}
            disabled={saving}
            className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
          >
            {t('catalog.discard', { defaultValue: 'Discard' })}
          </button>
        </div>
      )}
    </Section>
  )
}

/**
 * Copy a preset into a new, RETIRED one.
 *
 * A preset library's common move is "start from Standard and change two things",
 * and retyping eleven fields is where a wrong storage quota comes from. The copy
 * carries everything the original governs — caps, models, the freeze flag, the
 * billing rhythm — because a partial copy is worse than none: it looks finished.
 *
 * ── Why it lands INACTIVE, and unpublished ───────────────────────────────
 *
 * A newly duplicated preset is by definition half-configured — the operator
 * duplicated it in order to change something. Creating it active would make it
 * immediately selectable as a plan and (if billing is on and Publish is run)
 * sellable at the ORIGINAL's price. Retired is the reversible direction: one
 * checkbox turns it on once it says what the operator meant.
 *
 * `providerRefs` is deliberately NOT copied — the create route never accepts it.
 * Two plans sharing one Stripe Price would mean a change to either orphaning the
 * other's subscriptions.
 */
export async function duplicatePlan(plan: AdminPlanView, _existingIds: string[]): Promise<string> {
  // The id is generated server-side from the name now — `"<name> (copy)"` slugs
  // to `<slug>-copy`, suffixed on collision. The operator renames the NAME, not
  // the id, and the returned plan carries the real id.
  //
  // ── THE COPY IS BORN UNPRICED, DELIBERATELY ─────────────────────────
  //
  // `createPlan` takes no price — a plan's amounts live in `billing_plan_prices`
  // now — and this copies only the plan ROW, so the duplicate lands UNPRICED
  // (no `billing_plan_prices` row in any currency). That is the safe direction,
  // not an oversight: an unpriced plan REFUSES assignment rather than granting
  // nothing, and it lands INACTIVE too (below), so it is never sellable until
  // the operator prices it and switches it on. Copying the original's prices
  // would make the copy immediately sellable at the original's amount — exactly
  // what "retired + unpriced" is chosen to prevent. The operator prices the
  // copy in the currencies they mean to sell it in before activating it.
  const { plan: created } = await catalogAdminApi.createPlan({
    name: `${plan.name} (copy)`,
    audience: plan.audience,
    includedSeats: plan.includedSeats,
    storageQuotaBytes: plan.storageQuotaBytes,
    agentConcurrencyCap: plan.agentConcurrencyCap,
    // Carried like every other non-money plan field. Omitting it would land the
    // copy at the column default — an equal share, silently, on a duplicate the
    // operator made precisely to keep the original's shape (#539).
    fairnessWeight: plan.fairnessWeight,
    // Whole blob: the models list AND `canCreateContent` both live here, and
    // copying one key would silently drop the other.
    features: { ...(plan.features ?? {}) },
    cycleCount: plan.cycleCount ?? 1,
    cycleUnit: plan.cycleUnit ?? 'month',
    sortOrder: plan.sortOrder,
    isActive: false,
  })
  return created.id
}

/**
 * The preset's billing rhythm (D17), and the confirmation that stops it being an
 * invisible price change.
 *
 * ── Why this needs a confirmation at all ─────────────────────────────────
 *
 * The catalog states a price per seat, **per period**. Switching a preset from
 * monthly to weekly turns 99.00/month into 99.00/week — a ~4.3× increase with
 * nothing typed, no audit row saying the amount moved, and the number on screen
 * UNCHANGED. Of every money control on this page it is the only one where the
 * consequence is invisible in the field being edited, which is why the
 * confirmation ships with the control rather than after it.
 *
 * ── Two things it deliberately does NOT do ───────────────────────────────
 *
 *  1. **It never auto-converts the amount.** Dividing 99.00 by 4.348 to
 *     "preserve" the monthly total invents a price nobody typed and puts a
 *     rounded figure on a customer's invoice. The operator retypes the price if
 *     they meant to keep the total.
 *  2. **It does not touch the AI allowance.** That resets on the calendar month
 *     whatever the invoicing rhythm, because 'YYYY-MM' is the metering spine's
 *     storage format, not a setting. The confirmation says so, since "weekly
 *     billing" reads like "weekly everything".
 *
 * The affected-tenant count comes from the server, which resolves the plan
 * ladder with the shared predicate rather than re-spelling it as SQL.
 */
export function PlanCycle({
  plans,
  busy,
  onSaved,
  bar,
  savers,
  draftKey,
  t,
}: {
  plans: AdminPlanView[]
  busy: boolean
  onSaved: () => Promise<void>
  bar?: PlanEditBar
  /** Injected (P1 Task 3) — see `PlanUploads`' note on the single-plan bind. */
  savers: PlanSavers
  /** #897 — names this section's staged edits (scope + item) so they survive a
   *  phone/desktop layout swap. Absent = plain local state. */
  draftKey?: string
  t: T
}) {
  const toast = useToast()
  const confirm = useConfirm()
  const [drafts, setDrafts, { clear: clearDrafts }] = useDraftState<Record<string, { count: string; unit: string }>>(draftKey ?? null, NO_EDITS)
  const [saving, setSaving] = useState(false)

  const loadedCycle = (p: AdminPlanView) => ({ count: String(p.cycleCount ?? 1), unit: p.cycleUnit ?? 'month' })
  const cycleFor = (p: AdminPlanView) =>
    drafts[p.id] ?? { count: String(p.cycleCount ?? 1), unit: p.cycleUnit ?? 'month' }

  const changed = (p: AdminPlanView) => {
    const d = cycleFor(p)
    return Number(d.count) !== (p.cycleCount ?? 1) || d.unit !== (p.cycleUnit ?? 'month')
  }
  const dirty = plans.filter(changed)

  /** "every 2 weeks" / "monthly" — the rhythm in words, for the confirmation. */
  const rhythm = (count: number, unit: string) =>
    count === 1
      ? t(`catalog.plans.cycle.every.${unit}`, {
          defaultValue: { day: 'daily', week: 'weekly', month: 'monthly' }[unit] ?? unit,
        })
      : t('catalog.plans.cycle.everyN', {
          defaultValue: 'every {{count}} {{unit}}s', count, unit,
        })

  const save = async (batch: PinnedBatch = pinnedBatch()) => {
    // #1316 review round 2 (owner, 2026-10-28): the Save the person pressed FINISHES as the identity that pressed it
    // (`batch` — the bar's one batch, else this section's own); nothing is shown or reloaded once that session ended
    for (const p of dirty) {
      const d = cycleFor(p)
      const count = Number(d.count)
      if (!Number.isInteger(count) || count < 1) {
        const msg = t('catalog.plans.cycle.badCount', {
          defaultValue: 'The cycle count must be a whole number of 1 or more.',
        })
        // In bar mode the aggregate owns the toast; throwing halts the batch so
        // it does not report "Changes saved" over this rejection.
        if (bar) throw new Error(msg)
        toast.error(msg)
        return
      }
      // Ask the server who inherits this preset's rhythm BEFORE writing, so the
      // confirmation states a real blast radius rather than "some customers".
      let impact: Awaited<ReturnType<typeof catalogAdminApi.planCycleImpact>> | null = null
      try {
        impact = await batch.run(() => catalogAdminApi.planCycleImpact(p.id))
      } catch {
        // Advisory. A failed count must not block the edit, but the operator is
        // told the number is missing rather than shown a confident zero.
        impact = null
      }
      // From the IMPACT payload, not the plan row: the plan has no price of its
      // own, and the figure quoted in a confirmation must be the one the server
      // resolved for this deployment's currency. `null` = unpriced, and there
      // is then no amount for the rhythm to multiply.
      const price = impact && impact.seatPriceMinor != null
        ? formatCurrencyMinor(impact.seatPriceMinor, impact.currency)
        : null
      // never ASKED in the session after the Save's own: this rhythm (and the rest) stays as it was
      if (batch.ended()) { if (bar) throw new BarAbort(); return }
      const ok = await confirm({
        title: t('catalog.plans.cycle.confirmTitle', {
          defaultValue: 'Change {{plan}} to {{rhythm}}?', plan: p.name, rhythm: rhythm(count, d.unit),
        }),
        message: [
          // An UNPRICED plan has no amount for the rhythm to multiply, so the
          // sentence that sizes the change cannot be written — and writing it
          // with a zero would size it at nothing, which is the one reading that
          // makes the confirmation lie.
          price === null
            ? t('catalog.plans.cycle.confirmUnpriced', {
                defaultValue: 'This plan carries no price in the billing currency, so there is no amount to re-time. The rhythm still governs when a manually-billed customer\'s paid-through date advances.',
              })
            : t('catalog.plans.cycle.confirmPrice', {
                defaultValue: '{{price}} per seat is charged {{from}}. It will be charged {{to}} instead — the amount does not change, so this is a price change of its own.',
                price,
                from: rhythm(p.cycleCount ?? 1, p.cycleUnit ?? 'month'),
                to: rhythm(count, d.unit),
              }),
          impact === null
            ? t('catalog.plans.cycle.confirmUnknown', {
                defaultValue: 'The number of tenants this affects could not be read just now.',
              })
            : t('catalog.plans.cycle.confirmCount', {
                defaultValue: '{{count}} tenant inherits this rhythm and will be invoiced on the new one.',
                defaultValue_other: '{{count}} tenants inherit this rhythm and will be invoiced on the new one.',
                count: impact.inheriting,
              }),
          t('catalog.plans.cycle.confirmAllowance', {
            defaultValue: 'The AI allowance still resets on the calendar month — only invoicing moves.',
          }),
        ].join('\n\n'),
        confirmText: t('common:confirm'),
      })
      // Cancelling a mid-batch reprice must not let the aggregate report success.
      // In bar mode halt the whole batch (BarAbort → no green, no red); standalone
      // it just stops here.
      if (!ok) {
        if (bar) throw new BarAbort()
        return
      }

      setSaving(true)
      try {
        // Routed through the injected saver (P1 Task 3) — for
        // `scope.kind === 'plan'` it IS this same `catalogAdminApi.updatePlan` call.
        await savers.saveCycle(count, d.unit as 'day' | 'week' | 'month', batch)
      } catch (e: any) {
        if (bar) throw e // let the host bar halt the batch and report
        if (!batch.ended()) toast.error(e?.message || t('catalog.err.saveFailed'))
        setSaving(false)
        return
      } finally {
        setSaving(false)
      }
    }
    if (batch.ended()) { clearDrafts(); return }
    if (!bar) {
      toast.success(t('catalog.plans.cycle.saved', {
        count: dirty.length,
        defaultValue: 'Billing rhythm updated on {{count}} preset.',
        defaultValue_other: 'Billing rhythm updated on {{count}} presets.',
      }))
    }
    clearDrafts()
    await onSaved()
  }

  const discard = () => clearDrafts()
  useRegisterSection(bar, 'cycle', dirty.length, save, discard)

  if (plans.length === 0) return null

  return (
    <div className="mt-4">
      <div className="flex items-center gap-1.5 mb-2">
        <h4 className="themed-text text-xs font-medium">
          {t('catalog.plans.cycle.title', { defaultValue: 'Billing rhythm' })}
        </h4>
        <InfoHint text={t('catalog.plans.cycle.desc', {
          defaultValue:
            'How often the seat price is charged, and how far "Record payment" moves a manually-billed tenant\'s paid-through date. The price is stated PER PERIOD, so changing the rhythm changes what the tenant pays without the amount moving. The AI allowance is unaffected — it always resets on the calendar month.',
        })} />
      </div>
      <div className="themed-bg-secondary rounded-lg overflow-hidden">
        {plans.map((p) => {
          const d = cycleFor(p)
          return (
            <div
              key={p.id}
              className="flex items-center gap-2 px-3 py-2 border-b themed-border last:border-b-0"
            >
              <span className="themed-text text-sm flex-1 truncate">{p.name}</span>
              <span className="themed-text-muted text-[11px]">
                {t('catalog.plans.cycle.everyLabel', { defaultValue: 'every' })}
              </span>
              <input
                value={d.count}
                disabled={busy || saving}
                inputMode="numeric"
                aria-label={`${p.name} — ${t('catalog.plans.cycle.countLabel', { defaultValue: 'cycle count' })}`}
                onChange={(e) => setDrafts(cur => withEdit(cur, p.id, sameJson({ ...d, count: e.target.value }, loadedCycle(p)) ? undefined : { ...d, count: e.target.value }))}
                className="themed-input border rounded px-1.5 py-1 text-xs w-12 outline-none focus:border-blue-500 disabled:opacity-50"
              />
              <select
                value={d.unit}
                disabled={busy || saving}
                aria-label={`${p.name} — ${t('catalog.plans.cycle.unitLabel', { defaultValue: 'cycle unit' })}`}
                onChange={(e) => setDrafts(cur => withEdit(cur, p.id, sameJson({ ...d, unit: e.target.value }, loadedCycle(p)) ? undefined : { ...d, unit: e.target.value }))}
                className="themed-input border rounded px-1 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
              >
                {['day', 'week', 'month'].map(u => (
                  <option key={u} value={u}>
                    {t(`catalog.plans.cycle.unit.${u}`, { defaultValue: { day: 'days', week: 'weeks', month: 'months' }[u] })}
                  </option>
                ))}
              </select>
              {changed(p) && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 whitespace-nowrap">
                  {t('catalog.plans.cycle.repricesTenants', { defaultValue: 'reprices' })}
                </span>
              )}
            </div>
          )
        })}
      </div>
      {!bar && dirty.length > 0 && (
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => void save()}
            disabled={busy || saving}
            className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
          >
            {saving ? t('common:saving') : t('common:save')}
          </button>
          <button
            onClick={discard}
            disabled={saving}
            className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
          >
            {t('catalog.discard', { defaultValue: 'Discard' })}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Credit packs ─────────────────────────────────────────────────────

function PacksSection({
  packs,
  drafts,
  dirty,
  currencies,
  busy,
  onDraft,
  onSave,
  onDiscard,
  onCreated,
  defaultCurrency,
  t,
}: {
  packs: AdminPackView[]
  drafts: Record<string, PackDraft>
  dirty: string[]
  currencies: string[]
  busy: boolean
  onDraft: (id: string, patch: Partial<PackDraft>) => void
  onSave: () => void
  onDiscard: () => void
  onCreated: () => Promise<void>
  defaultCurrency: string
  t: T
}) {
  return (
    <Section title={t('catalog.packs.title')} desc={t('catalog.packs.desc')}>
      <div className="themed-bg-secondary rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b themed-border">
              {['id', 'name', 'price', 'credit', 'order', 'active', 'status'].map(col => (
                <th key={col} className="text-left px-2 py-2 themed-text-secondary text-xs font-medium whitespace-nowrap">
                  {t(`catalog.packs.col.${col}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {packs.map(k => {
              const d = drafts[k.id]
              if (!d) return null
              const price = parsePrice(d.priceText, d.currency)
              // Codex F13 (round 19): the preview MIRRORS the server's
              // face-value derivation — `currencyMinorToBillingMicro`,
              // no rate. The `rate` prop was removed with the retired
              // repricer; the pack draft's own `d.currency` is what
              // the row is denominated in. An earlier comment here
              // said the preview re-derived credit "at the CURRENT
              // rate" — that was the exact bug: a purchase now grants
              // its own face value (0177), and there is no rate to
              // apply. `catalog-routes.ts:1040/1088` does the same
              // arithmetic server-side.
              const preview = price == null ? null : previewCreditBillingMicro(price, d.currency)
              const changed = preview != null && preview !== k.creditBillingMicro
              // Same reason as the plan grid: the Save bar states a count, not
              // which rows.
              const isDirty = dirty.includes(k.id)
              return (
                <tr
                  key={k.id}
                  className={`border-b themed-border last:border-b-0 ${
                    isDirty ? 'bg-blue-500/10' : ''
                  }`}
                >
                  <td className="px-2 py-2 themed-text-muted font-mono text-[11px] whitespace-nowrap">
                    {isDirty && (
                      <span
                        aria-label={t('catalog.unsavedRow', { defaultValue: 'Unsaved changes' })}
                        title={t('catalog.unsavedRow', { defaultValue: 'Unsaved changes' })}
                        className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-blue-400 align-middle"
                      />
                    )}
                    {k.id}
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={d.name}
                      disabled={busy}
                      onChange={e => onDraft(k.id, { name: e.target.value })}
                      aria-label={`${d.name || k.id} ${t('catalog.packs.col.name')}`}
                      className="themed-input border rounded px-1.5 py-1 text-xs w-32 outline-none focus:border-blue-500 disabled:opacity-50"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1">
                      <CurrencySelect
                        value={d.currency}
                        currencies={currencies}
                        disabled={busy}
                        label={`${k.id} ${t('catalog.settings.currency')}`}
                        onChange={currency => onDraft(k.id, { currency })}
                      />
                      <input
                        value={d.priceText}
                        disabled={busy}
                        onChange={e => onDraft(k.id, { priceText: e.target.value })}
                        inputMode="decimal"
                        aria-label={`${d.name || k.id} ${t('catalog.packs.col.price')}`}
                        className="themed-input border rounded px-1.5 py-1 text-xs w-20 outline-none focus:border-blue-500 disabled:opacity-50"
                      />
                    </div>
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    <span className="themed-text text-xs">{fmtBillingMicro(preview ?? k.creditBillingMicro, d.currency)}</span>
                    {changed && (
                      <span className="themed-text-muted text-[10px] ml-1">
                        {t('catalog.packs.wasCredit', { was: fmtBillingMicro(k.creditBillingMicro, d.currency) })}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <input
                      value={d.sortText}
                      disabled={busy}
                      onChange={e => onDraft(k.id, { sortText: e.target.value })}
                      inputMode="numeric"
                      aria-label={`${d.name || k.id} ${t('catalog.packs.col.order')}`}
                      className="themed-input border rounded px-1.5 py-1 text-xs w-12 outline-none focus:border-blue-500 disabled:opacity-50"
                    />
                  </td>
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={d.isActive}
                      disabled={busy}
                      onChange={e => onDraft(k.id, { isActive: e.target.checked })}
                      aria-label={`${d.name || k.id} ${t('catalog.packs.col.active')}`}
                    />
                  </td>
                  <td className="px-2 py-2"><SyncBadge synced={k.syncedToProvider} t={t} /></td>
                </tr>
              )
            })}
            {packs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-2 py-3 themed-text-muted text-xs">{t('catalog.packs.empty')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <SaveBar count={dirty.length} busy={busy} onSave={onSave} onDiscard={onDiscard} t={t} />

      <NewPackForm busy={busy} defaultCurrency={defaultCurrency} currencies={currencies} onCreated={onCreated} t={t} />
    </Section>
  )
}

function NewPackForm({
  busy,
  defaultCurrency,
  currencies,
  onCreated,
  t,
}: {
  busy: boolean
  defaultCurrency: string
  currencies: string[]
  onCreated: () => Promise<void>
  t: T
}) {
  const toast = useToast()
  // The open form and its fields, kept across a phone/desktop layout swap (#897).
  const [open, setOpen, { clear: clearOpen }] = useDraftState('catalog:newPack:open', false)
  const [id, setId, { clear: clearId }] = useDraftState('catalog:newPack:id', '')
  const [name, setName, { clear: clearName }] = useDraftState('catalog:newPack:name', '')
  const [currency, setCurrency] = useDraftState('catalog:newPack:currency', defaultCurrency)
  const [priceText, setPriceText, { clear: clearPriceText }] = useDraftState('catalog:newPack:price', '')
  const [savingLocal, setSaving] = useState(false)
  // #897 — the create is in flight in the draft store: a form a layout swap
  // restored mid-request offers no second Create, and a create that finished
  // on the replaced screen reloads THIS list (its own went to the old one).
  const [createInFlight, trackCreate] = useDraftInFlight<boolean>('catalog:newPack', (o) => {
    if (!o.ok || !o.value) return
    clearOpen(); clearId(); clearName(); clearPriceText()
    void onCreated()
  })
  const saving = savingLocal || createInFlight

  const price = parsePrice(priceText, currency)
  // Codex F13 (round 19): face-value derivation, rate-free — the
  // `rate` prop was removed with the retired repricer.
  const preview = price != null && price > 0 ? previewCreditBillingMicro(price, currency) : null

  const submit = async () => {
    if (price == null || price < 1) { toast.error(t('catalog.err.packPrice', { id })); return }
    if (saving) return
    setSaving(true)
    await trackCreate(async () => { try {
      await catalogAdminApi.createPack({ id: id.trim(), name: name.trim(), currency, priceMinor: price })
      toast.success(t('catalog.toast.packCreated', { id }))
      clearOpen(); clearId(); clearName(); clearPriceText()
      await onCreated()
      return true
    } catch (e: any) {
      toast.error(e?.message || t('catalog.err.saveFailed'))
      return false
    } finally {
      setSaving(false)
    } })
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        disabled={busy}
        className="mt-2 text-xs themed-text-muted hover:themed-text underline underline-offset-2 disabled:opacity-50"
      >
        + {t('catalog.packs.add')}
      </button>
    )
  }

  return (
    <div className="themed-bg-secondary rounded-lg p-3 mt-2 flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1">
        <span className="themed-text-secondary text-[11px]">{t('catalog.packs.col.id')}</span>
        <input
          value={id}
          onChange={e => setId(e.target.value)}
          placeholder="ucp_100"
          aria-label={t('catalog.packs.col.id')}
          className="themed-input border rounded px-1.5 py-1 text-xs w-32 outline-none focus:border-blue-500"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="themed-text-secondary text-[11px]">{t('catalog.packs.col.name')}</span>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          aria-label={t('catalog.packs.col.name')}
          className="themed-input border rounded px-1.5 py-1 text-xs w-32 outline-none focus:border-blue-500"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="themed-text-secondary text-[11px]">{t('catalog.packs.col.price')}</span>
        <div className="flex items-center gap-1">
          <CurrencySelect value={currency} currencies={currencies} label={t('catalog.settings.currency')} onChange={setCurrency} />
          <input
            value={priceText}
            onChange={e => setPriceText(e.target.value)}
            inputMode="decimal"
            aria-label={t('catalog.packs.col.price')}
            className="themed-input border rounded px-1.5 py-1 text-xs w-20 outline-none focus:border-blue-500"
          />
        </div>
        {/* The button's promise, before it exists: what this price buys.
            Codex F13 (round 19): face-value, no rate — a purchase now
            grants its own denomination. */}
        <span className="themed-text-muted text-[10px]">
          {preview == null ? t('catalog.packs.creditPending') : t('catalog.packs.buys', { credit: fmtBillingMicro(preview, currency) })}
        </span>
      </label>
      <div className="flex items-center gap-2">
        <button
          onClick={submit}
          disabled={saving || !id.trim() || !name.trim()}
          className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
        >
          {saving ? t('catalog.saving') : t('catalog.packs.create')}
        </button>
        <button
          onClick={clearOpen}
          disabled={saving}
          className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
        >
          {t('catalog.cancel')}
        </button>
      </div>
    </div>
  )
}
